/**
 * Comprehensive Export/Import Manager for VARIORUM
 * 
 * This module provides unified export/import functionality for all workflow stages:
 * Books, Chapters, Witnesses, Sessions, and Alignments
 */

import JSZip from 'jszip'
import { useBooksStore } from '../store/booksStore'
import type { BookExport as LegacyBookExportV1, Witness } from '../store/booksStore'
import { useSegmentationStore } from '../store/segmentationStore'
import { useUnitEditStore } from '../store/unitEditStore'
import { useAlignmentStore } from '../store/alignmentStore'
import { useAnchoredRowsStore } from '../store/anchoredRowsStore'
import { useDeviceAnnotationStore } from '../store/deviceAnnotationStore'
import { useCharacterLayerStore } from '../store/characterLayerStore'
import { useReaderTextStructureStore } from '../store/readerTextStructureStore'
import { useStructuralRangeStore } from '../store/structuralRangeStore'
import { useAnnotationResearchNoteStore } from '../store/annotationResearchNoteStore'
import { useAnalyticalTaskStore } from '../store/analyticalTaskStore'
import { migrateAnalyticalTaskSet } from './analyticalTasks'
import { useSessionStore } from '../store/sessionStore'
import {
  LEGACY_COLLATION_STORE_KEY_PATHS,
  LEGACY_COLLATION_STORE_NAMES,
  collationStorage,
  useCollationStore,
  type LegacyCollationSnapshot,
} from '../store/collationStore'
import { parseManualClusterSyncRowId } from './manualAnchorStoreUtils'
import { runExclusiveBackupOperation } from './backupMutationCoordinator'
import { validateAppStateSnapshotInvariants } from './appStateSnapshotInvariants'
import {
  APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY,
  buildAppStateBackupWorkspaceInventory,
  clampBackupProgressPercent,
  type AppStateBackupProgressEvent,
  type AppStateBackupProgressListener,
  type AppStateBackupWorkspaceInventory,
} from './appStateBackupProgress'
import {
  PRIMARY_DB_STORE_KEY_PATHS,
  PRIMARY_DB_STORE_NAMES,
  dbStorage,
  type IndexedDbStoreKeyPath,
} from './indexedDBStorage'
import { RESUME_DB_STORE_KEY_PATHS, RESUME_DB_STORE_NAMES, resumeStorage } from './resumeStorage'
import { buildSourceObjectMatchKey } from './sourceObjectIdentity'
import { resolveLedgerLabelPrefix } from './contextLabelPrefix'
import {
  AppStateBackupExport,
  AppStateBundleChecksums,
  AppStateBundleFileRecord,
  AppStateBundleManifest,
  AppStateRouteSnapshot,
  BookExport,
  ChapterExport,
  WitnessExport,
  WorkflowSessionExport,
  AlignmentExport,
  ImportOptions,
  ImportResult,
  ExportFormat,
  ExportScope,
  ExportProgress,
  ImportProgress,
  ValidationResult,
  ProcessingStatistics,
  ModelUsageStats
} from '../types/exportTypes'
import type { SourceObject } from '../types/sourceObjects'

const PORTABLE_PRIMARY_STORE_NAMES = PRIMARY_DB_STORE_NAMES.filter(store => store !== 'ledger_safe_edit_events')
const FORENSIC_PRIMARY_STORE_NAMES = PRIMARY_DB_STORE_NAMES.filter(store => store === 'ledger_safe_edit_events')
const APP_STATE_BUNDLE_CHUNK_BYTES = 16 * 1024 * 1024
const APP_STATE_BUNDLE_EXPORT_TYPE = 'scholarly_app_bundle_v4'
const APP_STATE_BUNDLE_FORMAT_VERSION = '4.2.0'
const APP_STATE_STORE_CATALOG_VERSION = 'indexeddb-v30'
const PREVIOUS_APP_STATE_BUNDLE_FORMAT_VERSION = '4.1.0'
const PREVIOUS_APP_STATE_STORE_CATALOG_VERSION = 'indexeddb-v29'
const LEGACY_APP_STATE_BUNDLE_FORMAT_VERSION = '4.0.0'
const LEGACY_APP_STATE_STORE_CATALOG_VERSION = 'indexeddb-v25'
const WITNESS_PAYLOAD_STORE_NAMES = ['witness_text', 'witness_segmentation'] as const

const missingPrimaryStoresForBundleFormat = (formatVersion: string): Set<string> => {
  if (formatVersion === PREVIOUS_APP_STATE_BUNDLE_FORMAT_VERSION) {
    return new Set(WITNESS_PAYLOAD_STORE_NAMES)
  }
  if (formatVersion === LEGACY_APP_STATE_BUNDLE_FORMAT_VERSION) {
    return new Set<string>([
      ...WITNESS_PAYLOAD_STORE_NAMES,
      'chapter_structural_layers',
    ])
  }
  return new Set()
}

type AppStatePrimarySnapshot = Partial<Record<(typeof PRIMARY_DB_STORE_NAMES)[number], any[]>>
type AppStateResumeSnapshot = Partial<Record<(typeof RESUME_DB_STORE_NAMES)[number], any[]>>

interface PreparedAppStateRestore {
  primaryStores: AppStatePrimarySnapshot
  resumeStores: AppStateResumeSnapshot
  browserStorage: AppStateBackupExport['browser_storage']
  legacyCollationStores?: LegacyCollationSnapshot
  inventory: AppStateBackupWorkspaceInventory
}

function mergeSourceObjects(sourceObjects: SourceObject[], sourceObject: SourceObject): SourceObject[] {
  return sourceObjects.some(candidate => candidate.id === sourceObject.id)
    ? sourceObjects
    : [...sourceObjects, sourceObject]
}

export class ExportImportManager {
  
  // ==============================================================================
  // EXPORT FUNCTIONALITY
  // ==============================================================================

  /**
   * Export a complete book with all associated data
   */
  async exportBook(bookId: string, format: ExportFormat = 'json'): Promise<string | Blob> {
    const { getBook, getBookChapters, getChapterWitnesses, getWorkSourceObjects } = useBooksStore.getState()

    const book = getBook(bookId)
    if (!book) {
      throw new Error(`Book not found: ${bookId}`)
    }

    const chapters = getBookChapters(bookId)
    const witnesses: any[] = []
    const segmentationResults: Record<string, any> = {}
    const editSessions: Record<string, any> = {}
    const alignments: any[] = []

    // Collect all chapter data
    for (const chapter of chapters) {
      const chapterWitnesses = getChapterWitnesses(chapter.id)
      witnesses.push(...chapterWitnesses)

      // Collect segmentation results
      chapterWitnesses.forEach(witness => {
        if (witness.segmentation_result) {
          segmentationResults[witness.id] = witness.segmentation_result
        }
        if (witness.segmentation_runs) {
          Object.entries(witness.segmentation_runs).forEach(([runId, run]) => {
            segmentationResults[`${witness.id}_${runId}`] = run
          })
        }
      })

      // Note: Alignments and edit sessions collection temporarily disabled
      // until the correct store methods are implemented
    }

    const referencedSourceObjectIds = new Set(
      witnesses
        .map(witness => witness.source_object_id)
        .filter((sourceObjectId): sourceObjectId is string => typeof sourceObjectId === 'string' && sourceObjectId.length > 0)
    )
    const sourceObjects = getWorkSourceObjects(bookId)
      .filter(sourceObject => referencedSourceObjectIds.has(sourceObject.id))

    const processingStats = this.calculateProcessingStatistics(witnesses)

    const bookExport: BookExport = {
      format_version: '2.0.0',
      export_type: 'scholarly_book_v2',
      export_timestamp: new Date().toISOString(),
      exported_by: 'VARIORUM',
      metadata: {
        application_version: '2.0.0',
        platform: navigator.platform,
        compatibility_notes: ['Requires VARIORUM 2.0.0 or higher']
      },
      book,
      chapters,
      witnesses,
      source_objects: sourceObjects,
      segmentation_results: segmentationResults,
      edit_sessions: editSessions,
      alignments,
      export_summary: {
        total_chapters: chapters.length,
        total_witnesses: witnesses.length,
        total_segmentation_results: Object.keys(segmentationResults).length,
        total_edit_sessions: Object.keys(editSessions).length,
        total_alignments: alignments.length,
        processing_statistics: processingStats
      }
    }

    // Add integrity checksum
    const exportJson = JSON.stringify(bookExport, null, 2)
    bookExport.checksum = await this.calculateChecksum(exportJson)

    return this.formatExport(bookExport, format)
  }

  /**
   * Export a chapter with all associated data
   */
  async exportChapter(chapterId: string, format: ExportFormat = 'json'): Promise<string | Blob> {
    const { getChapter, getBook, getChapterWitnesses, getWorkSourceObjects } = useBooksStore.getState()
    const { loadChapterRows, getRowsForChapter } = useAnchoredRowsStore.getState()

    const chapter = getChapter(chapterId)
    if (!chapter) {
      throw new Error(`Chapter not found: ${chapterId}`)
    }

    const book = getBook(chapter.book_id)
    if (!book) {
      throw new Error(`Parent book not found: ${chapter.book_id}`)
    }

    const witnesses = getChapterWitnesses(chapterId)
    await loadChapterRows(chapterId)
    const anchoredRows = getRowsForChapter(chapterId)
    const segmentationResults: Record<string, any> = {}
    const editSessions: Record<string, any> = {}

    // Collect witness data
    witnesses.forEach(witness => {
      if (witness.segmentation_result) {
        segmentationResults[witness.id] = witness.segmentation_result
      }
      if (witness.segmentation_runs) {
        Object.entries(witness.segmentation_runs).forEach(([runId, run]) => {
          segmentationResults[`${witness.id}_${runId}`] = run
        })
      }
    })

    // Note: Edit sessions and alignments collection temporarily disabled
    // until the correct store methods are implemented
    const alignments: any[] = []
    const referencedSourceObjectIds = new Set(
      witnesses
        .map(witness => witness.source_object_id)
        .filter((sourceObjectId): sourceObjectId is string => typeof sourceObjectId === 'string' && sourceObjectId.length > 0)
    )
    const sourceObjects = getWorkSourceObjects(book.id)
      .filter(sourceObject => referencedSourceObjectIds.has(sourceObject.id))
    const processingStats = this.calculateProcessingStatistics(witnesses)

    const chapterExport: ChapterExport = {
      format_version: '2.0.0',
      export_type: 'scholarly_chapter_v2',
      export_timestamp: new Date().toISOString(),
      exported_by: 'VARIORUM',
      metadata: {
        application_version: '2.0.0',
        platform: navigator.platform,
        compatibility_notes: ['Requires VARIORUM 2.0.0 or higher']
      },
      book_reference: {
        book_id: book.id,
        book_name: book.name,
        book_description: book.description,
        export_timestamp: new Date().toISOString()
      },
      chapter,
      witnesses,
      source_objects: sourceObjects,
      segmentation_results: segmentationResults,
      edit_sessions: editSessions,
      alignments,
      anchored_rows: anchoredRows,
      export_summary: {
        total_witnesses: witnesses.length,
        completed_witnesses: witnesses.filter(w => w.processing_status === 'completed').length,
        processing_witnesses: witnesses.filter(w => 
          ['preprocessing', 'segmenting'].includes(w.processing_status)
        ).length,
        total_micro_units: this.countTotalMicroUnits(witnesses),
        average_confidence: this.calculateAverageConfidence(witnesses),
        processing_statistics: processingStats
      }
    }

    const exportJson = JSON.stringify(chapterExport, null, 2)
    chapterExport.checksum = await this.calculateChecksum(exportJson)

    return this.formatExport(chapterExport, format)
  }

  /**
   * Export a single witness with all associated data
   */
  async exportWitness(witnessId: string, format: ExportFormat = 'json'): Promise<string | Blob> {
    const { getWitness, getChapter, getBook, getWorkSourceObjects } = useBooksStore.getState()

    const witness = getWitness(witnessId)
    if (!witness) {
      throw new Error(`Witness not found: ${witnessId}`)
    }

    const chapter = getChapter(witness.chapter_id)
    const book = getBook(witness.book_id)

    if (!chapter || !book) {
      throw new Error(`Parent chapter or book not found`)
    }

    const segmentationResults: Record<string, any> = {}
    const editSessions: Record<string, any> = {}

    // Collect witness segmentation data
    if (witness.segmentation_result) {
      segmentationResults[witness.id] = witness.segmentation_result
    }
    if (witness.segmentation_runs) {
      Object.entries(witness.segmentation_runs).forEach(([runId, run]) => {
        segmentationResults[`${witness.id}_${runId}`] = run
      })
    }

    // Note: Edit sessions collection temporarily disabled
    // until the correct store methods are implemented
    const sourceObjects = witness.source_object_id
      ? getWorkSourceObjects(book.id).filter(sourceObject => sourceObject.id === witness.source_object_id)
      : []

    const witnessExport: WitnessExport = {
      format_version: '2.0.0',
      export_type: 'manuscript_witness_v2',
      export_timestamp: new Date().toISOString(),
      exported_by: 'VARIORUM',
      metadata: {
        application_version: '2.0.0',
        platform: navigator.platform
      },
      book_reference: {
        book_id: book.id,
        book_name: book.name,
        book_description: book.description,
        export_timestamp: new Date().toISOString()
      },
      chapter_reference: {
        chapter_id: chapter.id,
        chapter_name: chapter.name,
        chapter_description: chapter.description,
        order_index: chapter.order_index
      },
      witness,
      source_objects: sourceObjects,
      segmentation_results: segmentationResults,
      edit_sessions: editSessions,
      processing_history: this.extractProcessingHistory(witness),
      export_summary: {
        total_segmentation_runs: witness.segmentation_runs ? 
          Object.keys(witness.segmentation_runs).length : 0,
        total_micro_units: this.countWitnessMicroUnits(witness),
        total_edit_operations: 0, // TODO: Calculate when edit sessions store is available
        average_confidence: this.calculateWitnessConfidence(witness),
        processing_time_total: 0, // TODO: Calculate from processing history
        file_size_bytes: witness.uploaded_file?.size || 0
      }
    }

    const exportJson = JSON.stringify(witnessExport, null, 2)
    witnessExport.checksum = await this.calculateChecksum(exportJson)

    return this.formatExport(witnessExport, format)
  }

  /**
   * Export current workflow session
   */
  async exportWorkflowSession(sessionType: 'single_file' | 'batch' | 'witness', format: ExportFormat = 'json'): Promise<string | Blob> {
    const segStore = useSegmentationStore.getState()
    
    let sessionData: any = {}
    
    switch (sessionType) {
      case 'single_file':
        sessionData = {
          uploaded_file: segStore.uploadedFile,
          trigger_config: segStore.triggerConfig,
          preprocessing_result: segStore.preprocessingResult,
          selected_chunks: segStore.selectedChunks,
          llm_config: segStore.llmConfig,
          chunk_results: Object.fromEntries(segStore.chunkResults),
          processing_session: segStore.processingSession,
          workflow_state: {
            current_step: segStore.currentStep,
            completed_steps: [], // TODO: Track completed steps
            workflow_mode: segStore.workflowMode,
            last_modified: Date.now()
          }
        }
        break
      
      case 'batch':
        sessionData = {
          batch_files: segStore.batchFiles,
          trigger_config: segStore.triggerConfig,
          llm_config: segStore.llmConfig,
          batch_preprocessing_job: segStore.batchPreprocessingJob,
          batch_segmentation_job: segStore.batchSegmentationJob,
          batch_results: Object.fromEntries(segStore.chunkResults),
          workflow_state: {
            current_step: segStore.currentStep,
            completed_steps: [],
            workflow_mode: segStore.workflowMode,
            last_modified: Date.now()
          }
        }
        break
    }

    const sessionExport: WorkflowSessionExport = {
      format_version: '2.0.0',
      export_type: 'workflow_session_v2',
      export_timestamp: new Date().toISOString(),
      exported_by: 'VARIORUM',
      session_type: sessionType,
      session_data: sessionData,
      export_summary: {
        session_duration_minutes: 0, // TODO: Calculate session duration
        total_operations: 0, // TODO: Count operations
        data_size_bytes: JSON.stringify(sessionData).length,
        compatibility_version: '2.0.0'
      }
    }

    const exportJson = JSON.stringify(sessionExport, null, 2)
    sessionExport.checksum = await this.calculateChecksum(exportJson)

    return this.formatExport(sessionExport, format)
  }

  /**
   * Export the full persisted app state so it can be restored on another instance.
   */
  async exportAppState(
    format: ExportFormat = 'zip',
    onProgress?: AppStateBackupProgressListener
  ): Promise<string | Blob> {
    return runExclusiveBackupOperation(() => {
      this.reportBackupProgress(onProgress, {
        operation: 'export',
        phase: 'waiting-for-edits',
        percent: 2,
        message: 'Waiting for recent edits to finish',
        detail: 'This makes sure the backup includes your latest saved changes.',
      })
      return this.exportAppStateUnlocked(format, onProgress)
    })
  }

  private async exportAppStateUnlocked(
    format: ExportFormat,
    onProgress?: AppStateBackupProgressListener
  ): Promise<string | Blob> {
    this.reportBackupProgress(onProgress, {
      operation: 'export',
      phase: 'collecting-workspace',
      percent: 7,
      message: 'Collecting your works',
      detail: 'Works, contexts, witnesses, notes, and editing history are being gathered.',
    })
    const primaryStores = await dbStorage.exportAllStores()
    const resumeStores = await resumeStorage.exportAllData()
    const legacyCollationStores = format === 'zip' ? await collationStorage.exportAllData() : null
    const localStorageSnapshot = typeof window !== 'undefined' ? this.captureStorage(window.localStorage) : {}
    const sessionStorageSnapshot = typeof window !== 'undefined' ? this.captureStorage(window.sessionStorage) : {}
    const inventory = buildAppStateBackupWorkspaceInventory(primaryStores, {
      resumeStores,
      browserStorage: {
        local_storage: localStorageSnapshot,
        session_storage: sessionStorageSnapshot,
      },
      legacyCollationStores: legacyCollationStores || undefined,
      legacyCollationStatus: legacyCollationStores ? 'included' : 'not-present-preserved',
    })
    this.reportBackupProgress(onProgress, {
      operation: 'export',
      phase: 'cataloguing-works',
      percent: 22,
      message: inventory.works.length === 1 ? 'Found 1 Work' : `Found ${inventory.works.length} Works`,
      detail: 'Every saved browser item is grouped below, including shared or unassigned data.',
      inventory,
    })

    const routeSnapshot: AppStateRouteSnapshot = {
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      search: typeof window !== 'undefined' ? window.location.search : '',
      hash: typeof window !== 'undefined' ? window.location.hash : ''
    }

    const appExport: AppStateBackupExport = {
      format_version: '3.0.0',
      export_type: 'scholarly_app_state_v3',
      export_timestamp: new Date().toISOString(),
      exported_by: 'VARIORUM',
      metadata: {
        application_version: '3.0.0',
        platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
        compatibility_notes: [
          'Restores full persisted app state, including Workbench and Variorum records.',
          'Import is intended to replace current persisted state on the target instance.'
        ]
      },
      route_snapshot: routeSnapshot,
      primary_indexed_db: {
        stores: primaryStores
      },
      resume_indexed_db: {
        stores: resumeStores
      },
      browser_storage: {
        local_storage: localStorageSnapshot,
        session_storage: sessionStorageSnapshot
      },
      export_summary: {
        primary_store_counts: this.countSnapshotEntries(primaryStores),
        resume_store_counts: this.countSnapshotEntries(resumeStores),
        local_storage_keys: Object.keys(localStorageSnapshot).length,
        session_storage_keys: Object.keys(sessionStorageSnapshot).length
      }
    }

    if (format === 'zip') {
      return this.buildAppStateBackupZip(appExport, legacyCollationStores!, onProgress, inventory)
    }

    if (format === 'json') {
      this.reportBackupProgress(onProgress, {
        operation: 'export',
        phase: 'ready',
        percent: 98,
        message: 'Backup package is ready',
        inventory,
      })
      return this.buildAppStateBackupBlob(appExport)
    }

    return this.formatExport(appExport, format)
  }

  async importAppStateBackup(
    fileContent: string | ArrayBuffer,
    onProgress?: AppStateBackupProgressListener
  ): Promise<{ result: ImportResult; routeSnapshot: AppStateRouteSnapshot | null }> {
    return runExclusiveBackupOperation(() => this.importAppStateBackupUnlocked(fileContent, onProgress))
  }

  private async importAppStateBackupUnlocked(
    fileContent: string | ArrayBuffer,
    onProgress?: AppStateBackupProgressListener
  ): Promise<{ result: ImportResult; routeSnapshot: AppStateRouteSnapshot | null }> {
    this.reportBackupProgress(onProgress, {
      operation: 'import',
      phase: 'checking-backup',
      percent: 7,
      message: 'Opening the backup',
      detail: 'The backup is checked completely before anything in your workspace changes.',
    })
    if (typeof fileContent !== 'string' && this.isZipPayload(fileContent)) {
      return this.importAppStateBackupBundle(fileContent, onProgress)
    }

    let data: any
    if (typeof fileContent === 'string') {
      data = JSON.parse(fileContent)
    } else {
      data = JSON.parse(new TextDecoder().decode(new Uint8Array(fileContent)))
    }

    const result = await this.importAppStateBackupPayload(data as AppStateBackupExport, onProgress)
    return {
      result,
      routeSnapshot: result.success ? (data.route_snapshot || null) : null
    }
  }

  // ==============================================================================
  // IMPORT FUNCTIONALITY
  // ==============================================================================

  /**
   * Import data from exported file
   */
  async importData(fileContent: string | ArrayBuffer, options: ImportOptions): Promise<ImportResult> {
    try {
      let data: any
      
      if (typeof fileContent === 'string') {
        data = JSON.parse(fileContent)
      } else {
        // Handle binary data (ZIP files, etc.)
        throw new Error('Binary import not yet implemented')
      }

      // Validate import data
      const validation = await this.validateImportData(data)
      if (!validation.is_valid) {
        throw new Error(`Invalid import data: ${validation.validation_errors.join(', ')}`)
      }

      // Route to appropriate import handler
      switch (data.export_type) {
        case 'scholarly_book_v1':
          return await this.importLegacyBookV1(data as LegacyBookExportV1, options)
        case 'scholarly_book_v2':
          return await this.importBook(data as BookExport, options)
        case 'scholarly_chapter_v2':
          return await this.importChapter(data as ChapterExport, options)
        case 'manuscript_witness_v2':
          return await this.importWitness(data as WitnessExport, options)
        case 'workflow_session_v2':
          return await this.importWorkflowSession(data as WorkflowSessionExport, options)
        case 'text_alignment_v2':
          return await this.importAlignment(data as AlignmentExport, options)
        case 'scholarly_app_state_v3':
          return await this.importAppStateBackupPayload(data as AppStateBackupExport)
        case APP_STATE_BUNDLE_EXPORT_TYPE:
          throw new Error('Zip app-state bundles must be imported through the app-state backup flow')
        default:
          throw new Error(`Unsupported export type: ${data.export_type}`)
      }
    } catch (error) {
      return {
        success: false,
        imported_items: [],
        skipped_items: [],
        errors: [{
          type: 'format',
          message: error instanceof Error ? error.message : 'Unknown import error'
        }],
        import_summary: {
          total_items_processed: 0,
          successful_imports: 0,
          skipped_items: 0,
          errors: 1,
          import_duration_seconds: 0,
          data_integrity_verified: false
        },
        new_ids_mapping: {}
      }
    }
  }

  /**
   * Import a legacy (v1) book export
   *
   * v1 files are still produced by the legacy BooksDashboard exporter and use:
   * - export_type: scholarly_book_v1
   * - export_timestamp: number (ms)
   * - no format_version/checksum/metadata/export_summary
   */
  private async importLegacyBookV1(bookData: LegacyBookExportV1, options: ImportOptions): Promise<ImportResult> {
    const {
      createBook,
      createChapter,
      createWitness,
      updateWitnessStatus,
      createAlignmentSession,
      updateAlignmentResult,
    } = useBooksStore.getState()

    const result: ImportResult = {
      success: true,
      imported_items: [],
      skipped_items: [],
      errors: [],
      import_summary: {
        total_items_processed: 0,
        successful_imports: 0,
        skipped_items: 0,
        errors: 0,
        import_duration_seconds: 0,
        data_integrity_verified: !options.validate_integrity,
      },
      new_ids_mapping: {},
    }

    const startTime = Date.now()

    try {
      const bookId = await createBook(bookData.book.name, bookData.book.description)

      result.imported_items.push({
        type: 'book',
        original_id: bookData.book.id,
        new_id: bookId,
        name: bookData.book.name,
        status: 'imported',
      })
      result.new_ids_mapping[bookData.book.id] = bookId

      const chapterIdMap: Record<string, string> = {}
      for (const chapter of bookData.chapters || []) {
        const chapterId = await createChapter(bookId, chapter.name, chapter.description)
        chapterIdMap[chapter.id] = chapterId

        result.imported_items.push({
          type: 'chapter',
          original_id: chapter.id,
          new_id: chapterId,
          name: chapter.name,
          status: 'imported',
        })
        result.new_ids_mapping[chapter.id] = chapterId
      }

      const segmentationResultsByWitness = bookData.segmentation_results || {}
      const witnessIdMap: Record<string, string> = {}

      for (const witness of bookData.witnesses || []) {
        const targetChapterId = chapterIdMap[witness.chapter_id]
        if (!targetChapterId) {
          result.skipped_items.push({
            type: 'witness',
            id: witness.id,
            name: witness.sigla || witness.id,
            reason: `Missing chapter mapping for witness.chapter_id=${witness.chapter_id}`,
          })
          continue
        }

        const segmentationResult =
          witness.segmentation_result || segmentationResultsByWitness[witness.id]

        const witnessData: Omit<Witness, 'id' | 'chapter_id' | 'book_id' | 'created_at' | 'processing_status'> = {
          sigla: witness.sigla,
          description: witness.description,
          manuscript_date: witness.manuscript_date,
          library: witness.library,
          uploaded_file: witness.uploaded_file,
          preprocessing_result: witness.preprocessing_result,
          segmentation_result: segmentationResult,
          segmentation_runs: witness.segmentation_runs,
          active_segmentation_run: witness.active_segmentation_run,
          last_modified: Date.now(),
          version: 1,
        }

        const witnessId = await createWitness(targetChapterId, witnessData)
        witnessIdMap[witness.id] = witnessId
        result.new_ids_mapping[witness.id] = witnessId

        // Preserve processing status from export (critical for UI filtering)
        const fallbackStatus =
          segmentationResult ? 'completed' : witnessData.preprocessing_result ? 'preprocessed' : 'pending'
        await updateWitnessStatus(witnessId, witness.processing_status || fallbackStatus)

        result.imported_items.push({
          type: 'witness',
          original_id: witness.id,
          new_id: witnessId,
          name: witness.sigla,
          status: 'imported',
        })
      }

      // Import alignments (best-effort; results may require additional remapping)
      for (const alignment of bookData.alignments || []) {
        const targetChapterId = chapterIdMap[alignment.chapter_id]
        if (!targetChapterId) {
          result.skipped_items.push({
            type: 'alignment',
            id: alignment.id,
            name: alignment.id,
            reason: `Missing chapter mapping for alignment.chapter_id=${alignment.chapter_id}`,
          })
          continue
        }

        const newWitnessIds = (alignment.selected_witnesses || [])
          .map((id: string) => witnessIdMap[id])
          .filter(Boolean) as string[]

        if (newWitnessIds.length < 2) {
          result.skipped_items.push({
            type: 'alignment',
            id: alignment.id,
            name: alignment.id,
            reason: 'Not enough mapped witnesses to recreate alignment',
          })
          continue
        }

        const alignmentId = createAlignmentSession(targetChapterId, newWitnessIds)
        result.new_ids_mapping[alignment.id] = alignmentId

        if (alignment.alignment_result) {
          try {
            updateAlignmentResult(alignmentId, alignment.alignment_result as any)
          } catch (e) {
            console.warn('Failed to import alignment result (non-fatal):', e)
          }
        }

        result.imported_items.push({
          type: 'alignment',
          original_id: alignment.id,
          new_id: alignmentId,
          name: alignment.id,
          status: 'imported',
        })
      }

      result.import_summary.total_items_processed =
        result.imported_items.length + result.skipped_items.length
      result.import_summary.successful_imports = result.imported_items.length
      result.import_summary.skipped_items = result.skipped_items.length
      result.import_summary.errors = result.errors.length
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000
    } catch (error) {
      result.success = false
      result.errors.push({
        type: 'format',
        message: error instanceof Error ? error.message : 'Unknown import error',
      })
      result.import_summary.errors = result.errors.length
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000
    }

    return result
  }

  /**
   * Import a complete book
   */
  private async importBook(bookData: BookExport, options: ImportOptions): Promise<ImportResult> {
    const { createBook, createChapter, createWitness } = useBooksStore.getState()
    
    const result: ImportResult = {
      success: true,
      imported_items: [],
      skipped_items: [],
      errors: [],
      import_summary: {
        total_items_processed: 0,
        successful_imports: 0,
        skipped_items: 0,
        errors: 0,
        import_duration_seconds: 0,
        data_integrity_verified: true
      },
      new_ids_mapping: {}
    }

    const startTime = Date.now()

    try {
      // Import book - await completion
      const bookId = await createBook(bookData.book.name, bookData.book.description)
      
      result.imported_items.push({
        type: 'book',
        original_id: bookData.book.id,
        new_id: bookId,
        name: bookData.book.name,
        status: 'imported'
      })
      result.new_ids_mapping[bookData.book.id] = bookId

      const sourceObjectIdMap = await this.importSourceObjectsForWork(bookId, bookData.source_objects)

      // Import chapters - await completion for each
      for (const chapter of bookData.chapters) {
        const chapterId = await createChapter(bookId, chapter.name, chapter.description)

        result.imported_items.push({
          type: 'chapter',
          original_id: chapter.id,
          new_id: chapterId,
          name: chapter.name,
          status: 'imported'
        })
        result.new_ids_mapping[chapter.id] = chapterId

        // Import witnesses for this chapter - await completion for each
        const chapterWitnesses = bookData.witnesses.filter(w => w.chapter_id === chapter.id)
        for (const witness of chapterWitnesses) {
          const witnessData = {
            sigla: witness.sigla,
            description: witness.description,
            manuscript_date: witness.manuscript_date,
            library: witness.library,
            city: witness.city,
            shelfmark: witness.shelfmark,
            source_object_id: witness.source_object_id ? sourceObjectIdMap[witness.source_object_id] : undefined,
            witness_type: witness.witness_type,
            transcription_type: witness.transcription_type,
            coverage_status: witness.coverage_status,
            lacunose_status: witness.lacunose_status,
            uploaded_file: witness.uploaded_file,
            preprocessing_result: witness.preprocessing_result,
            segmentation_result: witness.segmentation_result,
            segmentation_runs: witness.segmentation_runs,
            active_segmentation_run: witness.active_segmentation_run,
            last_modified: Date.now(),
            version: 1
          }
          const witnessId = await createWitness(chapterId, witnessData)

          result.imported_items.push({
            type: 'witness',
            original_id: witness.id,
            new_id: witnessId,
            name: witness.sigla,
            status: 'imported'
          })
          result.new_ids_mapping[witness.id] = witnessId
        }
      }

      result.import_summary.total_items_processed = result.imported_items.length
      result.import_summary.successful_imports = result.imported_items.length
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000

    } catch (error) {
      result.success = false
      result.errors.push({
        type: 'format',
        message: error instanceof Error ? error.message : 'Unknown import error'
      })
      result.import_summary.errors = 1
    }

    return result
  }

  /**
   * Import a chapter
   */
  private async importChapter(chapterData: ChapterExport, options: ImportOptions): Promise<ImportResult> {
    const { getBook, getChapter, createBook, createChapter, updateChapter, createWitness } = useBooksStore.getState()
    
    const result: ImportResult = {
      success: true,
      imported_items: [],
      skipped_items: [],
      errors: [],
      import_summary: {
        total_items_processed: 0,
        successful_imports: 0,
        skipped_items: 0,
        errors: 0,
        import_duration_seconds: 0,
        data_integrity_verified: true
      },
      new_ids_mapping: {}
    }

    const startTime = Date.now()

    try {
      let targetBookId = options.target_book_id
      let targetChapterId = options.target_chapter_id

      const existingChapter = targetChapterId ? getChapter(targetChapterId) : undefined
      if (existingChapter) {
        targetChapterId = existingChapter.id
        if (!targetBookId) {
          targetBookId = existingChapter.book_id
        }
      } else {
        targetChapterId = undefined
      }

      let existingBook = targetBookId ? getBook(targetBookId) : undefined
      if (!existingBook && existingChapter) {
        existingBook = getBook(existingChapter.book_id)
        if (existingBook) {
          targetBookId = existingBook.id
        }
      }

      // Create or find target book
      if (!targetBookId || !existingBook) {
        targetBookId = await createBook(
          chapterData.book_reference.book_name,
          chapterData.book_reference.book_description || 'Imported book'
        )
        existingBook = getBook(targetBookId) || undefined
        result.imported_items.push({
          type: 'book',
          original_id: chapterData.book_reference.book_id,
          new_id: targetBookId,
          name: chapterData.book_reference.book_name,
          status: 'imported'
        })
      } else {
        result.imported_items.push({
          type: 'book',
          original_id: chapterData.book_reference.book_id,
          new_id: targetBookId,
          name: existingBook.name,
          status: 'updated'
        })
      }
      result.new_ids_mapping[chapterData.book_reference.book_id] = targetBookId

      const sourceObjectIdMap = await this.importSourceObjectsForWork(targetBookId, chapterData.source_objects)

      let chapterId = targetChapterId
      if (chapterId) {
        result.imported_items.push({
          type: 'chapter',
          original_id: chapterData.chapter.id,
          new_id: chapterId,
          name: existingChapter?.name || chapterData.chapter.name,
          status: 'updated'
        })
      } else {
        chapterId = await createChapter(targetBookId, chapterData.chapter.name, chapterData.chapter.description)
        result.imported_items.push({
          type: 'chapter',
          original_id: chapterData.chapter.id,
          new_id: chapterId,
          name: chapterData.chapter.name,
          status: 'imported'
        })
      }
      result.new_ids_mapping[chapterData.chapter.id] = chapterId

      if (!chapterId) {
        throw new Error('Failed to resolve target chapter for import')
      }
      const resolvedChapterId = chapterId
      const chapterLabelPrefix = resolveLedgerLabelPrefix({
        contextLabelPrefix: (chapterData.chapter as any).label_prefix,
        contextTitle: chapterData.chapter.name,
        witnessSigla: chapterData.witnesses.map(witness => witness.sigla),
        fallbackPrefix: 'Cx'
      })
      await updateChapter(resolvedChapterId, { label_prefix: chapterLabelPrefix })

      const segmentationResultsByWitness = chapterData.segmentation_results || {}

      // Import witnesses - await completion for each
      for (const witness of chapterData.witnesses) {
        const segmentationResult =
          witness.segmentation_result || segmentationResultsByWitness[witness.id]

        const witnessData = {
          sigla: witness.sigla,
          description: witness.description,
          manuscript_date: witness.manuscript_date,
          library: witness.library,
          city: witness.city,
          shelfmark: witness.shelfmark,
          source_object_id: witness.source_object_id ? sourceObjectIdMap[witness.source_object_id] : undefined,
          witness_type: witness.witness_type,
          transcription_type: witness.transcription_type,
          coverage_status: witness.coverage_status,
          lacunose_status: witness.lacunose_status,
          uploaded_file: witness.uploaded_file,
          preprocessing_result: witness.preprocessing_result,
          segmentation_result: segmentationResult,
          segmentation_runs: witness.segmentation_runs,
          active_segmentation_run: witness.active_segmentation_run,
          last_modified: Date.now(),
          version: 1
        }
        const witnessId = await createWitness(resolvedChapterId, witnessData)
        
        // Update witness status to reflect imported state
        const { updateWitnessStatus } = useBooksStore.getState()
        await updateWitnessStatus(witnessId, witness.processing_status || 'pending')

        result.imported_items.push({
          type: 'witness',
          original_id: witness.id,
          new_id: witnessId,
          name: witness.sigla,
          status: 'imported'
        })
        result.new_ids_mapping[witness.id] = witnessId
      }

      const rawAnchoredRows = Array.isArray((chapterData as any).anchored_rows)
        ? ((chapterData as any).anchored_rows as any[])
        : (Array.isArray((chapterData as any).anchoredRows)
          ? ((chapterData as any).anchoredRows as any[])
          : [])

      if (rawAnchoredRows.length > 0) {
        type ImportedAnchoredUnitMapping = {
          witnessId: string
          unitUuid: string
          unitLabel?: string
          textSnapshot?: string
          confidence?: number
          excluded?: boolean
          addedAt: string
        }
        type ImportedAnchoredRow = {
          id: string
          chapterId: string
          labelPrefix: string
          labelNumber: number
          position: number
          unitMappings: ImportedAnchoredUnitMapping[]
          createdAt: string
          updatedAt: string
          createdBy: string
          status: 'auto' | 'confirmed' | 'needs-review'
        }

        const { upsertRows } = useAnchoredRowsStore.getState()
        const { loadChapterRows, getRowsForChapter, deleteRow } = useAnchoredRowsStore.getState()
        const nowIso = new Date().toISOString()

        await loadChapterRows(resolvedChapterId)
        const existingChapterRows = getRowsForChapter(resolvedChapterId)
        const staleImportedRows = existingChapterRows.filter(row => !parseManualClusterSyncRowId(row.id))
        for (const staleRow of staleImportedRows) {
          await deleteRow(staleRow.id, resolvedChapterId)
        }

        const importedRows = rawAnchoredRows
          .map((rawRow, index): ImportedAnchoredRow | null => {
            const rawMappings = Array.isArray(rawRow?.unitMappings) ? rawRow.unitMappings : []
            const unitMappings = rawMappings
              .map((mapping: any): ImportedAnchoredUnitMapping | null => {
                const originalWitnessId = typeof mapping?.witnessId === 'string' ? mapping.witnessId : ''
                const remappedWitnessId = result.new_ids_mapping[originalWitnessId] || originalWitnessId
                const unitUuid = typeof mapping?.unitUuid === 'string'
                  ? mapping.unitUuid.trim()
                  : (typeof mapping?.unit_uuid === 'string' ? mapping.unit_uuid.trim() : '')
                if (!remappedWitnessId || !unitUuid) {
                  return null
                }
                return {
                  witnessId: remappedWitnessId,
                  unitUuid,
                  unitLabel: typeof mapping?.unitLabel === 'string' ? mapping.unitLabel : undefined,
                  textSnapshot: typeof mapping?.textSnapshot === 'string' ? mapping.textSnapshot : undefined,
                  confidence: typeof mapping?.confidence === 'number' ? mapping.confidence : undefined,
                  excluded: typeof mapping?.excluded === 'boolean' ? mapping.excluded : undefined,
                  addedAt: typeof mapping?.addedAt === 'string' ? mapping.addedAt : nowIso
                }
              })
              .filter((mapping: ImportedAnchoredUnitMapping | null): mapping is ImportedAnchoredUnitMapping => Boolean(mapping))

            if (unitMappings.length === 0) {
              return null
            }

            const rawStatus = typeof rawRow?.status === 'string' ? rawRow.status : 'auto'
            const status = rawStatus === 'confirmed' || rawStatus === 'needs-review' ? rawStatus : 'auto'
            const position = Number.isFinite(rawRow?.position)
              ? Math.max(1, Math.floor(rawRow.position))
              : (index + 1)

            return {
              id: `generated_alignment::${resolvedChapterId}::${position}`,
              chapterId: resolvedChapterId,
              labelPrefix: resolveLedgerLabelPrefix({
                contextLabelPrefix: chapterLabelPrefix,
                secondaryPrefix: typeof rawRow?.labelPrefix === 'string' ? rawRow.labelPrefix : undefined,
                witnessSigla: chapterData.witnesses.map(witness => witness.sigla),
                fallbackPrefix: 'Cx'
              }),
              labelNumber: Number.isFinite(rawRow?.labelNumber)
                ? Math.max(1, Math.floor(rawRow.labelNumber))
                : (index + 1),
              position,
              unitMappings,
              createdAt: typeof rawRow?.createdAt === 'string' ? rawRow.createdAt : nowIso,
              updatedAt: nowIso,
              createdBy: typeof rawRow?.createdBy === 'string' ? rawRow.createdBy : 'chapter-import',
              status
            }
          })
          .filter((row: ImportedAnchoredRow | null): row is ImportedAnchoredRow => Boolean(row))

        if (importedRows.length > 0) {
          await upsertRows(resolvedChapterId, importedRows as any, { autoRelabel: false, labelPrefix: chapterLabelPrefix })
          result.imported_items.push({
            type: 'alignment',
            original_id: `anchored_rows:${chapterData.chapter.id}`,
            new_id: `anchored_rows:${resolvedChapterId}`,
            name: `Anchored rows (${importedRows.length})`,
            status: 'imported'
          })
        }
      }
      
      // Import alignments
      const { createAlignmentSession } = useBooksStore.getState()
      for (const alignment of chapterData.alignments || []) {
        const newWitnessIds = alignment.selected_witnesses
          .map(id => result.new_ids_mapping[id])
          .filter(Boolean) as string[]
        
        if (newWitnessIds.length > 0) {
          const alignmentId = createAlignmentSession(resolvedChapterId, newWitnessIds)
          
          // TODO: Import alignment results if available
          if (alignment.alignment_result) {
            // This would require updating the alignment result with the new alignment ID
            console.log('Alignment results import not yet implemented for alignment:', alignmentId)
          }
          
          result.imported_items.push({
            type: 'alignment',
            original_id: alignment.id,
            new_id: alignmentId,
            name: `Alignment for ${newWitnessIds.length} witnesses`,
            status: 'imported'
          })
        }
      }

      result.import_summary.total_items_processed = result.imported_items.length
      result.import_summary.successful_imports = result.imported_items.length
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000

    } catch (error) {
      result.success = false
      result.errors.push({
        type: 'format',
        message: error instanceof Error ? error.message : 'Unknown import error'
      })
      result.import_summary.errors = 1
    }

    return result
  }

  /**
   * Import a witness
   */
  private async importWitness(witnessData: WitnessExport, options: ImportOptions): Promise<ImportResult> {
    const { getBook, getChapter, createBook, createChapter, createWitness } = useBooksStore.getState()
    
    const result: ImportResult = {
      success: true,
      imported_items: [],
      skipped_items: [],
      errors: [],
      import_summary: {
        total_items_processed: 0,
        successful_imports: 0,
        skipped_items: 0,
        errors: 0,
        import_duration_seconds: 0,
        data_integrity_verified: true
      },
      new_ids_mapping: {}
    }

    const startTime = Date.now()

    try {
      let targetBookId = options.target_book_id
      let targetChapterId = options.target_chapter_id

      // Create or find target book
      if (!targetBookId) {
        targetBookId = await createBook(
          witnessData.book_reference.book_name,
          witnessData.book_reference.book_description || 'Imported book'
        )
        
        result.imported_items.push({
          type: 'book',
          original_id: witnessData.book_reference.book_id,
          new_id: targetBookId,
          name: witnessData.book_reference.book_name,
          status: 'imported'
        })
      }

      // Create or find target chapter
      if (!targetChapterId) {
        targetChapterId = await createChapter(
          targetBookId,
          witnessData.chapter_reference.chapter_name,
          witnessData.chapter_reference.chapter_description || 'Imported chapter'
        )
        
        result.imported_items.push({
          type: 'chapter',
          original_id: witnessData.chapter_reference.chapter_id,
          new_id: targetChapterId,
          name: witnessData.chapter_reference.chapter_name,
          status: 'imported'
        })
      }

      const sourceObjectIdMap = await this.importSourceObjectsForWork(targetBookId, witnessData.source_objects)

      // Import witness - await completion
      const witnessDataForCreate = {
        sigla: witnessData.witness.sigla,
        description: witnessData.witness.description,
        manuscript_date: witnessData.witness.manuscript_date,
        library: witnessData.witness.library,
        city: witnessData.witness.city,
        shelfmark: witnessData.witness.shelfmark,
        source_object_id: witnessData.witness.source_object_id ? sourceObjectIdMap[witnessData.witness.source_object_id] : undefined,
        witness_type: witnessData.witness.witness_type,
        transcription_type: witnessData.witness.transcription_type,
        coverage_status: witnessData.witness.coverage_status,
        lacunose_status: witnessData.witness.lacunose_status,
        uploaded_file: witnessData.witness.uploaded_file,
        preprocessing_result: witnessData.witness.preprocessing_result,
        segmentation_result: witnessData.witness.segmentation_result,
        segmentation_runs: witnessData.witness.segmentation_runs,
        active_segmentation_run: witnessData.witness.active_segmentation_run,
        last_modified: Date.now(),
        version: 1
      }
      const witnessId = await createWitness(targetChapterId, witnessDataForCreate)
      
      // Update witness status to reflect imported state
      const { updateWitnessStatus } = useBooksStore.getState()
      await updateWitnessStatus(witnessId, witnessData.witness.processing_status || 'pending')

      result.imported_items.push({
        type: 'witness',
        original_id: witnessData.witness.id,
        new_id: witnessId,
        name: witnessData.witness.sigla,
        status: 'imported'
      })
      result.new_ids_mapping[witnessData.witness.id] = witnessId

      result.import_summary.total_items_processed = result.imported_items.length
      result.import_summary.successful_imports = result.imported_items.length
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000

    } catch (error) {
      result.success = false
      result.errors.push({
        type: 'format',
        message: error instanceof Error ? error.message : 'Unknown import error'
      })
      result.import_summary.errors = 1
    }

    return result
  }

  /**
   * Import a workflow session
   */
  private async importWorkflowSession(sessionData: WorkflowSessionExport, options: ImportOptions): Promise<ImportResult> {
    const segStore = useSegmentationStore.getState()
    
    const result: ImportResult = {
      success: true,
      imported_items: [],
      skipped_items: [],
      errors: [],
      import_summary: {
        total_items_processed: 0,
        successful_imports: 0,
        skipped_items: 0,
        errors: 0,
        import_duration_seconds: 0,
        data_integrity_verified: true
      },
      new_ids_mapping: {}
    }

    const startTime = Date.now()

    try {
      const data = sessionData.session_data as any

      switch (sessionData.session_type) {
        case 'single_file':
          // Restore single file workflow state
          if (data.uploaded_file) {
            segStore.setUploadedFile(data.uploaded_file)
          }
          if (data.trigger_config) {
            segStore.updateTriggerConfig(data.trigger_config)
          }
          if (data.preprocessing_result) {
            segStore.setPreprocessingResult(data.preprocessing_result)
          }
          if (data.selected_chunks) {
            segStore.setSelectedChunks(data.selected_chunks)
          }
          // Note: LLM config setter not available in current store
          if (data.chunk_results) {
            Object.entries(data.chunk_results).forEach(([chunkId, result]) => {
              segStore.updateChunkResult(chunkId, result as any)
            })
          }
          if (data.workflow_state) {
            segStore.setCurrentStep(data.workflow_state.current_step)
            segStore.setWorkflowMode(data.workflow_state.workflow_mode)
          }
          break

        case 'batch':
          // Restore batch workflow state
          if (data.batch_files) {
            segStore.setBatchFiles(data.batch_files)
          }
          if (data.trigger_config) {
            segStore.updateTriggerConfig(data.trigger_config)
          }
          // Note: LLM config setter not available in current store
          if (data.batch_preprocessing_job) {
            segStore.setBatchPreprocessingJob(data.batch_preprocessing_job)
          }
          if (data.batch_segmentation_job) {
            segStore.setBatchSegmentationJob(data.batch_segmentation_job)
          }
          if (data.batch_results) {
            Object.entries(data.batch_results).forEach(([chunkId, result]) => {
              segStore.updateChunkResult(chunkId, result as any)
            })
          }
          if (data.workflow_state) {
            segStore.setCurrentStep(data.workflow_state.current_step)
            segStore.setWorkflowMode(data.workflow_state.workflow_mode)
          }
          break

        default:
          throw new Error(`Unsupported session type: ${sessionData.session_type}`)
      }

      result.imported_items.push({
        type: 'session',
        original_id: 'workflow_session',
        new_id: 'current_session',
        name: `${sessionData.session_type} session`,
        status: 'imported'
      })

      result.import_summary.total_items_processed = 1
      result.import_summary.successful_imports = 1
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000

    } catch (error) {
      result.success = false
      result.errors.push({
        type: 'format',
        message: error instanceof Error ? error.message : 'Unknown session import error'
      })
      result.import_summary.errors = 1
    }

    return result
  }

  /**
   * Import alignment data
   */
  private async importAlignment(alignmentData: AlignmentExport, options: ImportOptions): Promise<ImportResult> {
    // Note: Alignment import temporarily disabled until correct store methods are implemented
    
    const result: ImportResult = {
      success: true,
      imported_items: [],
      skipped_items: [],
      errors: [],
      import_summary: {
        total_items_processed: 0,
        successful_imports: 0,
        skipped_items: 0,
        errors: 0,
        import_duration_seconds: 0,
        data_integrity_verified: true
      },
      new_ids_mapping: {}
    }

    const startTime = Date.now()

    try {
      // Alignment import not yet implemented due to store method availability
      result.success = false
      result.errors.push({
        type: 'format',
        message: 'Alignment import not yet implemented'
      })
      result.import_summary.errors = 1
    } catch (error) {
      result.success = false
      result.errors.push({
        type: 'format',
        message: error instanceof Error ? error.message : 'Unknown alignment import error'
      })
      result.import_summary.errors = 1
    }

    return result
  }

  private async importAppStateBackupPayload(
    appStateData: AppStateBackupExport,
    onProgress?: AppStateBackupProgressListener
  ): Promise<ImportResult> {
    const result: ImportResult = {
      success: true,
      imported_items: [],
      skipped_items: [],
      errors: [],
      import_summary: {
        total_items_processed: 0,
        successful_imports: 0,
        skipped_items: 0,
        errors: 0,
        import_duration_seconds: 0,
        data_integrity_verified: true
      },
      new_ids_mapping: {}
    }

    const startTime = Date.now()

    try {
      const prepared = this.prepareLegacyAppStateRestore(appStateData, onProgress)
      await this.replaceAppStateWithRollback(prepared, onProgress)

      result.imported_items.push({
        type: 'app_state',
        original_id: 'scholarly_app_state_v3',
        new_id: 'current_app_state',
        name: 'Full app state',
        status: 'imported'
      })
      result.import_summary.total_items_processed = 1
      result.import_summary.successful_imports = 1
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000
      this.reportBackupProgress(onProgress, {
        operation: 'import',
        phase: 'ready',
        percent: 98,
        message: 'Your restored workspace is ready',
        inventory: prepared.inventory,
      })
    } catch (error) {
      result.success = false
      result.errors.push({
        type: 'format',
        message: error instanceof Error ? error.message : 'Unknown app-state import error'
      })
      result.import_summary.errors = result.errors.length
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000
    }

    return result
  }

  private async importAppStateBackupBundle(
    fileContent: ArrayBuffer,
    onProgress?: AppStateBackupProgressListener
  ): Promise<{ result: ImportResult; routeSnapshot: AppStateRouteSnapshot | null }> {
    const zip = await JSZip.loadAsync(fileContent)
    this.reportBackupProgress(onProgress, {
      operation: 'import',
      phase: 'checking-backup',
      percent: 10,
      message: 'Checking the backup',
      detail: 'Every part is verified before the restore begins.',
    })
    const manifest = await this.readBundleJson<AppStateBundleManifest>(zip, 'manifest.json')
    const checksums = await this.readBundleJson<AppStateBundleChecksums>(zip, 'checksums.json')
    const result = await this.importAppStateBackupBundlePayload(zip, manifest, checksums, onProgress)

    return {
      result,
      routeSnapshot: result.success ? manifest.route_snapshot : null
    }
  }

  private async importAppStateBackupBundlePayload(
    zip: JSZip,
    manifest: AppStateBundleManifest,
    checksums: AppStateBundleChecksums,
    onProgress?: AppStateBackupProgressListener
  ): Promise<ImportResult> {
    const result: ImportResult = {
      success: true,
      imported_items: [],
      skipped_items: [],
      errors: [],
      import_summary: {
        total_items_processed: 0,
        successful_imports: 0,
        skipped_items: 0,
        errors: 0,
        import_duration_seconds: 0,
        data_integrity_verified: true
      },
      new_ids_mapping: {}
    }

    const startTime = Date.now()

    try {
      const prepared = await this.prepareAppStateBundleRestore(zip, manifest, checksums, onProgress)
      await this.replaceAppStateWithRollback(prepared, onProgress)

      result.imported_items.push({
        type: 'app_state',
        original_id: APP_STATE_BUNDLE_EXPORT_TYPE,
        new_id: 'current_app_state',
        name: 'Full app bundle',
        status: 'imported'
      })
      result.import_summary.total_items_processed = 1
      result.import_summary.successful_imports = 1
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000
      this.reportBackupProgress(onProgress, {
        operation: 'import',
        phase: 'ready',
        percent: 98,
        message: 'Your restored workspace is ready',
        inventory: prepared.inventory,
      })
    } catch (error) {
      result.success = false
      result.errors.push({
        type: 'format',
        message: error instanceof Error ? error.message : 'Unknown app-bundle import error'
      })
      result.import_summary.errors = result.errors.length
      result.import_summary.import_duration_seconds = (Date.now() - startTime) / 1000
      result.import_summary.data_integrity_verified = false
    }

    return result
  }

  private prepareLegacyAppStateRestore(
    appStateData: AppStateBackupExport,
    onProgress?: AppStateBackupProgressListener
  ): PreparedAppStateRestore {
    if (appStateData?.export_type !== 'scholarly_app_state_v3' || appStateData?.format_version !== '3.0.0') {
      throw new Error('Unsupported app-state backup format')
    }

    const primaryStores = this.normalizeDatabaseSnapshot(
      appStateData.primary_indexed_db?.stores,
      PRIMARY_DB_STORE_NAMES,
      'primary',
      true,
      WITNESS_PAYLOAD_STORE_NAMES
    ) as AppStatePrimarySnapshot
    const resumeStores = this.normalizeDatabaseSnapshot(
      appStateData.resume_indexed_db?.stores,
      RESUME_DB_STORE_NAMES,
      'resume',
      true
    ) as AppStateResumeSnapshot
    const browserStorage = this.validateBrowserStorageSnapshot(appStateData.browser_storage)

    this.validateDeclaredSnapshotCounts(
      appStateData.export_summary?.primary_store_counts,
      primaryStores,
      'primary',
      true,
      WITNESS_PAYLOAD_STORE_NAMES
    )
    this.validateDeclaredSnapshotCounts(
      appStateData.export_summary?.resume_store_counts,
      resumeStores,
      'resume',
      true
    )

    this.validateSnapshotStoreKeys(primaryStores, PRIMARY_DB_STORE_KEY_PATHS, 'primary')
    this.validateSnapshotStoreKeys(resumeStores, RESUME_DB_STORE_KEY_PATHS, 'resume')
    validateAppStateSnapshotInvariants(primaryStores)
    const inventory = buildAppStateBackupWorkspaceInventory(primaryStores, {
      resumeStores,
      browserStorage,
      legacyCollationStatus: 'not-present-preserved',
    })
    this.reportBackupProgress(onProgress, {
      operation: 'import',
      phase: 'cataloguing-works',
      percent: 55,
      message: inventory.works.length === 1 ? 'Found 1 Work in this backup' : `Found ${inventory.works.length} Works in this backup`,
      detail: 'Every saved browser item is grouped below. This older file does not contain older CollationDB records.',
      inventory,
    })

    return { primaryStores, resumeStores, browserStorage, inventory }
  }

  private async prepareAppStateBundleRestore(
    zip: JSZip,
    manifest: AppStateBundleManifest,
    checksums: AppStateBundleChecksums,
    onProgress?: AppStateBackupProgressListener
  ): Promise<PreparedAppStateRestore> {
    this.validateAppStateBundleManifest(zip, manifest, checksums)
    const totalPayloadBytes = manifest.files.reduce((total, file) => total + file.bytes, 0)
    let checkedPayloadBytes = 0
    const reportCheckedFile = (file: AppStateBundleFileRecord): void => {
      checkedPayloadBytes += file.bytes
      const ratio = totalPayloadBytes > 0 ? checkedPayloadBytes / totalPayloadBytes : 1
      this.reportBackupProgress(onProgress, {
        operation: 'import',
        phase: 'checking-backup',
        percent: 12 + (ratio * 38),
        message: 'Checking every part of the backup',
        detail: `${Math.round(ratio * 100)}% checked — your current workspace has not been changed.`,
      })
    }

    const primaryStores = Object.fromEntries(
      PRIMARY_DB_STORE_NAMES.map(store => [store, []])
    ) as AppStatePrimarySnapshot
    const resumeStores = Object.fromEntries(
      RESUME_DB_STORE_NAMES.map(store => [store, []])
    ) as AppStateResumeSnapshot
    const legacyCollationStores = manifest.compatibility_summary
      ? Object.fromEntries(LEGACY_COLLATION_STORE_NAMES.map(store => [store, []])) as unknown as LegacyCollationSnapshot
      : undefined

    for (const store of PRIMARY_DB_STORE_NAMES) {
      const files = manifest.files
        .filter(file => file.storage_scope === 'primary' && file.store_name === store)
        .sort((left, right) => left.path.localeCompare(right.path))
      const records: any[] = []
      for (const file of files) {
        const chunk = await this.readBundleNdjson(zip, file.path, checksums, file.bytes)
        if (chunk.length !== file.record_count) {
          throw new Error(`Record count mismatch for bundle entry: ${file.path}`)
        }
        records.push(...chunk)
        reportCheckedFile(file)
      }
      primaryStores[store] = records as any
    }

    for (const store of RESUME_DB_STORE_NAMES) {
      const files = manifest.files
        .filter(file => file.storage_scope === 'resume' && file.store_name === store)
        .sort((left, right) => left.path.localeCompare(right.path))
      const records: any[] = []
      for (const file of files) {
        const chunk = await this.readBundleNdjson(zip, file.path, checksums, file.bytes)
        if (chunk.length !== file.record_count) {
          throw new Error(`Record count mismatch for bundle entry: ${file.path}`)
        }
        records.push(...chunk)
        reportCheckedFile(file)
      }
      resumeStores[store] = records
    }

    if (legacyCollationStores) {
      for (const store of LEGACY_COLLATION_STORE_NAMES) {
        const files = manifest.files
          .filter(file => file.storage_scope === 'legacy_collation' && file.store_name === store)
          .sort((left, right) => left.path.localeCompare(right.path))
        const records: any[] = []
        for (const file of files) {
          const chunk = await this.readBundleNdjson(zip, file.path, checksums, file.bytes)
          if (chunk.length !== file.record_count) {
            throw new Error(`Record count mismatch for bundle entry: ${file.path}`)
          }
          records.push(...chunk)
          reportCheckedFile(file)
        }
        legacyCollationStores[store] = records
      }
    }

    const browserFile = manifest.files.find(file => file.path === 'portable/browser-storage.json')!
    const browserStorage = this.validateBrowserStorageSnapshot(
      await this.readBundleJson<AppStateBackupExport['browser_storage']>(
        zip,
        browserFile.path,
        checksums,
        browserFile.bytes
      )
    )
    reportCheckedFile(browserFile)
    const routeFile = manifest.files.find(file => file.path === 'portable/route.json')!
    const routeSnapshot = await this.readBundleJson<AppStateRouteSnapshot>(
      zip,
      routeFile.path,
      checksums,
      routeFile.bytes
    )
    reportCheckedFile(routeFile)
    if (JSON.stringify(routeSnapshot) !== JSON.stringify(manifest.route_snapshot)) {
      throw new Error('Route snapshot does not match the bundle manifest')
    }

    this.validatePreparedBundleCounts(manifest, primaryStores, resumeStores, legacyCollationStores)
    this.validateSnapshotStoreKeys(primaryStores, PRIMARY_DB_STORE_KEY_PATHS, 'primary')
    this.validateSnapshotStoreKeys(resumeStores, RESUME_DB_STORE_KEY_PATHS, 'resume')
    if (legacyCollationStores) {
      this.validateSnapshotStoreKeys(
        legacyCollationStores,
        LEGACY_COLLATION_STORE_KEY_PATHS,
        'legacy CollationDB'
      )
    }
    validateAppStateSnapshotInvariants(primaryStores)
    const inventory = buildAppStateBackupWorkspaceInventory(primaryStores, {
      resumeStores,
      browserStorage,
      legacyCollationStores,
      legacyCollationStatus: legacyCollationStores ? 'included' : 'not-present-preserved',
    })
    this.reportBackupProgress(onProgress, {
      operation: 'import',
      phase: 'cataloguing-works',
      percent: 55,
      message: inventory.works.length === 1 ? 'Found 1 Work in this backup' : `Found ${inventory.works.length} Works in this backup`,
      detail: 'Every saved browser item passed its checks and is grouped below.',
      inventory,
    })
    return { primaryStores, resumeStores, browserStorage, legacyCollationStores, inventory }
  }

  private validateAppStateBundleManifest(
    zip: JSZip,
    manifest: AppStateBundleManifest,
    checksums: AppStateBundleChecksums
  ): void {
    const isCurrentFormat = manifest?.format_version === APP_STATE_BUNDLE_FORMAT_VERSION
    const isPreviousFormat = manifest?.format_version === PREVIOUS_APP_STATE_BUNDLE_FORMAT_VERSION
    const isLegacyFormat = manifest?.format_version === LEGACY_APP_STATE_BUNDLE_FORMAT_VERSION
    if (manifest?.export_type !== APP_STATE_BUNDLE_EXPORT_TYPE
      || (!isCurrentFormat && !isPreviousFormat && !isLegacyFormat)) {
      throw new Error('Unsupported app-bundle format')
    }
    if (!Array.isArray(manifest.files)) {
      throw new Error('Invalid app-bundle manifest: files must be an array')
    }
    if (!checksums || typeof checksums !== 'object' || !checksums.files || typeof checksums.files !== 'object') {
      throw new Error('Invalid app-bundle checksum catalog')
    }
    const supportedCatalog = !manifest.store_catalog_version
      || (isCurrentFormat && manifest.store_catalog_version === APP_STATE_STORE_CATALOG_VERSION)
      || (isPreviousFormat && manifest.store_catalog_version === PREVIOUS_APP_STATE_STORE_CATALOG_VERSION)
      || (isLegacyFormat && manifest.store_catalog_version === LEGACY_APP_STATE_STORE_CATALOG_VERSION)
    if (!supportedCatalog) {
      throw new Error(`Unsupported app-bundle store catalog: ${manifest.store_catalog_version}`)
    }
    // Every supported format must declare its complete format-specific store
    // catalog. Only stores introduced after that format are normalized empty;
    // arbitrary omissions are never treated as intentional during replacement.
    const strictStoreCatalog = true
    const missingPrimaryStores = missingPrimaryStoresForBundleFormat(manifest.format_version)
    const expectedPortablePrimaryStores = PORTABLE_PRIMARY_STORE_NAMES.filter(
      store => !missingPrimaryStores.has(store)
    )

    const portablePrimary = new Set<string>(PORTABLE_PRIMARY_STORE_NAMES)
    const forensicPrimary = new Set<string>(FORENSIC_PRIMARY_STORE_NAMES)
    const resumeStores = new Set<string>(RESUME_DB_STORE_NAMES)
    const legacyCollationStores = new Set<string>(LEGACY_COLLATION_STORE_NAMES)
    const paths = new Set<string>()

    for (const file of manifest.files) {
      if (!file || typeof file.path !== 'string' || file.path.length === 0 || file.path.includes('..')) {
        throw new Error('Invalid bundle entry path')
      }
      if (paths.has(file.path)) {
        throw new Error(`Duplicate bundle entry declaration: ${file.path}`)
      }
      paths.add(file.path)
      if (!zip.file(file.path)) {
        throw new Error(`Bundle entry not found: ${file.path}`)
      }
      if (!Number.isSafeInteger(file.record_count) || file.record_count < 0
        || !Number.isSafeInteger(file.bytes) || file.bytes < 0) {
        throw new Error(`Invalid bundle entry metadata: ${file.path}`)
      }
      if (typeof checksums.files[file.path] !== 'string' || !/^[a-f0-9]{64}$/i.test(checksums.files[file.path])) {
        throw new Error(`Missing checksum for bundle entry: ${file.path}`)
      }

      if (file.storage_scope === 'primary') {
        const store = file.store_name || ''
        const validSection = (file.section === 'portable' && portablePrimary.has(store))
          || (file.section === 'forensic' && forensicPrimary.has(store))
        if (!validSection || file.kind !== 'ndjson') {
          throw new Error(`Invalid primary store declaration: ${file.path}`)
        }
        this.validateStoreChunkPath(file.path, file.section, 'primary', store)
      } else if (file.storage_scope === 'resume') {
        const store = file.store_name || ''
        if (file.section !== 'portable' || !resumeStores.has(store) || file.kind !== 'ndjson') {
          throw new Error(`Invalid resume store declaration: ${file.path}`)
        }
        this.validateStoreChunkPath(file.path, 'portable', 'resume', store)
      } else if (file.storage_scope === 'legacy_collation') {
        const store = file.store_name || ''
        if (!manifest.compatibility_summary
          || file.section !== 'compatibility'
          || !legacyCollationStores.has(store)
          || file.kind !== 'ndjson') {
          throw new Error(`Invalid legacy CollationDB store declaration: ${file.path}`)
        }
        this.validateStoreChunkPath(file.path, 'compatibility', 'legacy_collation', store)
      } else {
        const isRoute = file.path === 'portable/route.json'
          && file.section === 'portable'
          && file.storage_scope === 'route'
        const isBrowser = file.path === 'portable/browser-storage.json'
          && file.section === 'portable'
          && file.storage_scope === 'browser'
        if ((!isRoute && !isBrowser) || file.kind !== 'json' || file.record_count !== 1 || file.store_name) {
          throw new Error(`Invalid non-store bundle declaration: ${file.path}`)
        }
      }
    }

    for (const checksumPath of Object.keys(checksums.files)) {
      if (!paths.has(checksumPath)) {
        throw new Error(`Checksum catalog contains undeclared entry: ${checksumPath}`)
      }
    }
    for (const requiredPath of ['portable/route.json', 'portable/browser-storage.json']) {
      if (!paths.has(requiredPath)) {
        throw new Error(`Missing required bundle entry: ${requiredPath}`)
      }
    }

    this.validateStoreCountCatalog(
      manifest.portable_summary?.primary_store_counts,
      expectedPortablePrimaryStores,
      'primary',
      strictStoreCatalog
    )
    this.validateStoreCountCatalog(
      manifest.forensic_summary?.primary_store_counts,
      FORENSIC_PRIMARY_STORE_NAMES,
      'forensic primary',
      strictStoreCatalog
    )
    this.validateStoreCountCatalog(
      manifest.portable_summary?.resume_store_counts,
      RESUME_DB_STORE_NAMES,
      'resume',
      strictStoreCatalog
    )
    if (manifest.compatibility_summary) {
      this.validateStoreCountCatalog(
        manifest.compatibility_summary.legacy_collation_store_counts,
        LEGACY_COLLATION_STORE_NAMES,
        'legacy CollationDB',
        true
      )
    } else if (manifest.files.some(file => file.section === 'compatibility' || file.storage_scope === 'legacy_collation')) {
      throw new Error('Legacy CollationDB files require a compatibility summary')
    }
  }

  private validateStoreChunkPath(
    path: string,
    section: 'portable' | 'forensic' | 'compatibility',
    scope: 'primary' | 'resume' | 'legacy_collation',
    store: string
  ): void {
    const escapedStore = store.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const expected = new RegExp(`^${section}/${scope}/${escapedStore}/part-\\d{4}\\.ndjson$`)
    if (!expected.test(path)) {
      throw new Error(`Invalid store chunk path: ${path}`)
    }
  }

  private validateStoreCountCatalog(
    catalog: Record<string, number> | undefined,
    expectedStores: readonly string[],
    label: string,
    requireAll: boolean
  ): void {
    if (!catalog || typeof catalog !== 'object') {
      throw new Error(`Missing ${label} store catalog`)
    }
    const expected = new Set(expectedStores)
    for (const store of expectedStores) {
      if (!Object.prototype.hasOwnProperty.call(catalog, store)) {
        if (requireAll) {
          throw new Error(`Missing ${label} store declaration: ${store}`)
        }
        continue
      }
      if (!Number.isSafeInteger(catalog[store]) || catalog[store] < 0) {
        throw new Error(`Invalid ${label} store count: ${store}`)
      }
    }
    for (const store of Object.keys(catalog)) {
      if (!expected.has(store)) {
        throw new Error(`Unknown ${label} store declaration: ${store}`)
      }
    }
  }

  private validatePreparedBundleCounts(
    manifest: AppStateBundleManifest,
    primaryStores: AppStatePrimarySnapshot,
    resumeStores: AppStateResumeSnapshot,
    legacyCollationStores?: LegacyCollationSnapshot
  ): void {
    const missingPrimaryStores = missingPrimaryStoresForBundleFormat(manifest.format_version)
    for (const store of PORTABLE_PRIMARY_STORE_NAMES) {
      const actual = primaryStores[store]?.length || 0
      const expected = manifest.portable_summary.primary_store_counts[store]
      if (missingPrimaryStores.has(store) && expected === undefined && actual === 0) {
        continue
      }
      if (expected === undefined || actual !== expected) {
        throw new Error(`Primary store count mismatch: ${store}`)
      }
    }
    for (const store of FORENSIC_PRIMARY_STORE_NAMES) {
      const actual = primaryStores[store]?.length || 0
      const expected = manifest.forensic_summary.primary_store_counts[store]
      if (expected === undefined || actual !== expected) {
        throw new Error(`Forensic store count mismatch: ${store}`)
      }
    }
    for (const store of RESUME_DB_STORE_NAMES) {
      const actual = resumeStores[store]?.length || 0
      const expected = manifest.portable_summary.resume_store_counts[store]
      if (expected === undefined || actual !== expected) {
        throw new Error(`Resume store count mismatch: ${store}`)
      }
    }
    if (legacyCollationStores && manifest.compatibility_summary) {
      for (const store of LEGACY_COLLATION_STORE_NAMES) {
        const actual = legacyCollationStores[store]?.length || 0
        if (actual !== manifest.compatibility_summary.legacy_collation_store_counts[store]) {
          throw new Error(`Legacy CollationDB store count mismatch: ${store}`)
        }
      }
    }
  }

  private normalizeDatabaseSnapshot(
    snapshot: Record<string, any[]> | undefined,
    expectedStores: readonly string[],
    label: string,
    requireAll = false,
    allowedMissingStores: readonly string[] = []
  ): Record<string, any[]> {
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
      throw new Error(`Invalid ${label} database snapshot`)
    }
    const expected = new Set(expectedStores)
    const allowedMissing = new Set(allowedMissingStores)
    if (requireAll) {
      for (const store of expectedStores) {
        if (!Object.prototype.hasOwnProperty.call(snapshot, store) && !allowedMissing.has(store)) {
          throw new Error(`Missing ${label} store: ${store}`)
        }
      }
    }
    for (const [store, records] of Object.entries(snapshot)) {
      if (!expected.has(store)) {
        throw new Error(`Unknown ${label} store: ${store}`)
      }
      if (!Array.isArray(records)) {
        throw new Error(`Invalid ${label} store payload: ${store}`)
      }
    }
    return Object.fromEntries(expectedStores.map(store => [store, snapshot[store] || []]))
  }

  private serializeStoreRecordKey(
    record: unknown,
    keyPath: IndexedDbStoreKeyPath,
    label: string,
    store: string,
    recordIndex: number
  ): string {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      throw new Error(`${label} store ${store} record ${recordIndex + 1} is not an object`)
    }
    const fields = Array.isArray(keyPath) ? keyPath : [keyPath]
    const values = fields.map(field => {
      const value = (record as Record<string, unknown>)[field]
      const validString = typeof value === 'string' && value.length > 0
      const validNumber = typeof value === 'number' && Number.isFinite(value)
      if (!validString && !validNumber) {
        throw new Error(`${label} store ${store} record ${recordIndex + 1} has an invalid ${field} key`)
      }
      return [typeof value, value]
    })
    return JSON.stringify(values)
  }

  private snapshotStoreKeys(
    records: any[],
    keyPath: IndexedDbStoreKeyPath,
    label: string,
    store: string
  ): string[] {
    const keys = records.map((record, index) => (
      this.serializeStoreRecordKey(record, keyPath, label, store, index)
    ))
    const seen = new Set<string>()
    keys.forEach(key => {
      if (seen.has(key)) {
        throw new Error(`Duplicate ${label} store key in ${store}: ${key}`)
      }
      seen.add(key)
    })
    return keys
  }

  private validateSnapshotStoreKeys(
    snapshot: Record<string, any[]>,
    keyPaths: Record<string, IndexedDbStoreKeyPath>,
    label: string
  ): void {
    for (const [store, keyPath] of Object.entries(keyPaths)) {
      this.snapshotStoreKeys(Array.isArray(snapshot[store]) ? snapshot[store] : [], keyPath, label, store)
    }
  }

  private verifyRestoredSnapshotKeys(
    expected: Record<string, any[]>,
    actual: Record<string, any[]>,
    keyPaths: Record<string, IndexedDbStoreKeyPath>,
    label: string
  ): void {
    for (const [store, keyPath] of Object.entries(keyPaths)) {
      const expectedKeys = this.snapshotStoreKeys(expected[store] || [], keyPath, label, store).sort()
      const actualKeys = this.snapshotStoreKeys(actual[store] || [], keyPath, `restored ${label}`, store).sort()
      if (expectedKeys.length !== actualKeys.length
        || expectedKeys.some((key, index) => key !== actualKeys[index])) {
        throw new Error(`Post-restore key verification failed for ${label} store ${store}`)
      }
    }
  }

  private validateDeclaredSnapshotCounts(
    declared: Record<string, number> | undefined,
    snapshot: Record<string, any[]>,
    label: string,
    requireAll = false,
    allowedMissingStores: readonly string[] = []
  ): void {
    if (!declared || typeof declared !== 'object') {
      if (requireAll) {
        throw new Error(`Missing ${label} store count catalog`)
      }
      return
    }
    const allowedMissing = new Set(allowedMissingStores)
    if (requireAll) {
      for (const store of Object.keys(snapshot)) {
        if (!Object.prototype.hasOwnProperty.call(declared, store) && !allowedMissing.has(store)) {
          throw new Error(`Missing ${label} store count: ${store}`)
        }
      }
    }
    for (const [store, count] of Object.entries(declared)) {
      if (!Object.prototype.hasOwnProperty.call(snapshot, store)) {
        throw new Error(`Unknown ${label} store count: ${store}`)
      }
      if (!Number.isSafeInteger(count) || count < 0 || snapshot[store].length !== count) {
        throw new Error(`${label} store count mismatch: ${store}`)
      }
    }
  }

  private validateBrowserStorageSnapshot(
    snapshot: AppStateBackupExport['browser_storage'] | undefined
  ): AppStateBackupExport['browser_storage'] {
    const validatePartition = (partition: unknown, label: string): Record<string, string> => {
      if (!partition || typeof partition !== 'object' || Array.isArray(partition)) {
        throw new Error(`Invalid ${label} storage snapshot`)
      }
      const validated: Record<string, string> = {}
      for (const [key, value] of Object.entries(partition)) {
        if (typeof key !== 'string' || typeof value !== 'string') {
          throw new Error(`Invalid ${label} storage entry`)
        }
        if (key !== APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY) validated[key] = value
      }
      return validated
    }

    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
      throw new Error('Invalid browser storage snapshot')
    }
    return {
      local_storage: validatePartition(snapshot.local_storage, 'local'),
      session_storage: validatePartition(snapshot.session_storage, 'session')
    }
  }

  private async replaceAppStateWithRollback(
    prepared: PreparedAppStateRestore,
    onProgress?: AppStateBackupProgressListener
  ): Promise<void> {
    this.reportBackupProgress(onProgress, {
      operation: 'import',
      phase: 'protecting-current-work',
      percent: 60,
      message: 'Protecting your current workspace',
      detail: 'A recovery copy is made before the restore starts.',
      inventory: prepared.inventory,
    })
    return this.replaceAppStateWithRollbackUnlocked(prepared, onProgress)
  }

  private async replaceAppStateWithRollbackUnlocked(
    prepared: PreparedAppStateRestore,
    onProgress?: AppStateBackupProgressListener
  ): Promise<void> {
    const previous: Omit<PreparedAppStateRestore, 'inventory'> = {
      primaryStores: await dbStorage.exportAllStores(),
      resumeStores: await resumeStorage.exportAllData(),
      browserStorage: {
        local_storage: this.captureStorage(window.localStorage),
        session_storage: this.captureStorage(window.sessionStorage)
      },
    }
    if (prepared.legacyCollationStores) {
      previous.legacyCollationStores = await collationStorage.exportAllData()
    }

    try {
      this.reportBackupProgress(onProgress, {
        operation: 'import',
        phase: 'restoring-workspace',
        percent: 70,
        message: 'Restoring your Works',
        detail: 'Contexts, witnesses, alignments, Character Index entries, and notes are restored together.',
        inventory: prepared.inventory,
      })
      await dbStorage.replaceAllStores(prepared.primaryStores as any)
      this.verifyRestoredSnapshotKeys(
        prepared.primaryStores,
        await dbStorage.exportAllStores(),
        PRIMARY_DB_STORE_KEY_PATHS,
        'primary'
      )
      this.reportBackupProgress(onProgress, {
        operation: 'import',
        phase: 'restoring-workspace',
        percent: 82,
        message: 'Restoring reading and resume information',
        inventory: prepared.inventory,
      })
      await resumeStorage.replaceAllData(prepared.resumeStores)
      this.verifyRestoredSnapshotKeys(
        prepared.resumeStores,
        await resumeStorage.exportAllData(),
        RESUME_DB_STORE_KEY_PATHS,
        'resume'
      )
      if (prepared.legacyCollationStores) {
        await collationStorage.replaceAllData(prepared.legacyCollationStores)
        this.verifyRestoredSnapshotKeys(
          prepared.legacyCollationStores,
          await collationStorage.exportAllData(),
          LEGACY_COLLATION_STORE_KEY_PATHS,
          'legacy CollationDB'
        )
      }
      this.restoreStorage(window.localStorage, prepared.browserStorage.local_storage)
      this.restoreStorage(window.sessionStorage, prepared.browserStorage.session_storage)
      this.reportBackupProgress(onProgress, {
        operation: 'import',
        phase: 'reopening-workspace',
        percent: 92,
        message: 'Reopening the restored workspace',
        detail: 'The app is reconnecting its views to the restored Works.',
        inventory: prepared.inventory,
      })
      await this.refreshHydratedStores()
    } catch (error) {
      this.reportBackupProgress(onProgress, {
        operation: 'import',
        phase: 'recovering-current-work',
        percent: null,
        message: 'The restore could not finish — recovering your previous workspace',
        detail: 'The app is putting back the recovery copy made before the restore.',
        inventory: prepared.inventory,
      })
      const rollbackFailures: string[] = []
      const attemptRollback = async (label: string, operation: () => void | Promise<void>): Promise<void> => {
        try {
          await operation()
        } catch (rollbackError) {
          rollbackFailures.push(`${label}: ${rollbackError instanceof Error ? rollbackError.message : 'unknown error'}`)
        }
      }
      // Compensation is deliberately best-effort across every storage boundary.
      // A failure restoring one database must not prevent the remaining live data
      // from being returned to its pre-import state.
      await attemptRollback('primary IndexedDB', () => dbStorage.replaceAllStores(previous.primaryStores as any))
      await attemptRollback('resume IndexedDB', () => resumeStorage.replaceAllData(previous.resumeStores))
      if (previous.legacyCollationStores) {
        await attemptRollback('legacy CollationDB', () => collationStorage.replaceAllData(previous.legacyCollationStores!))
      }
      await attemptRollback('localStorage', () => this.restoreStorage(window.localStorage, previous.browserStorage.local_storage))
      await attemptRollback('sessionStorage', () => this.restoreStorage(window.sessionStorage, previous.browserStorage.session_storage))
      await attemptRollback('hydrated application stores', () => this.refreshHydratedStores())
      if (rollbackFailures.length > 0) {
        const originalMessage = error instanceof Error ? error.message : 'Unknown restore error'
        throw new Error(`${originalMessage}; automatic rollback was incomplete: ${rollbackFailures.join('; ')}`)
      }
      throw error
    }
  }

  private async importSourceObjectsForWork(
    workId: string,
    exportedSourceObjects?: SourceObject[]
  ): Promise<Record<string, string>> {
    if (!Array.isArray(exportedSourceObjects) || exportedSourceObjects.length === 0) {
      return {}
    }

    let hydratedSourceObjects = useBooksStore.getState().getWorkSourceObjects(workId)
    const sourceObjectIdMap: Record<string, string> = {}

    for (const exportedSourceObject of exportedSourceObjects) {
      const normalizedMatchKey = exportedSourceObject.normalized_match_key || buildSourceObjectMatchKey(exportedSourceObject)

      let resolvedSourceObject = normalizedMatchKey
        ? hydratedSourceObjects.find(sourceObject => (
            sourceObject.work_id === workId && sourceObject.normalized_match_key === normalizedMatchKey
          ))
        : undefined

      if (!resolvedSourceObject && normalizedMatchKey) {
        resolvedSourceObject = await dbStorage.getSourceObjectByMatchKey(workId, normalizedMatchKey) || undefined
      }

      if (!resolvedSourceObject) {
        const createdAt = Date.now()
        resolvedSourceObject = {
          ...exportedSourceObject,
          id: crypto.randomUUID(),
          work_id: workId,
          normalized_match_key: normalizedMatchKey || undefined,
          created_at: createdAt,
          last_modified: createdAt,
          version: 1
        }
        await dbStorage.saveSourceObject(resolvedSourceObject)
      }

      hydratedSourceObjects = mergeSourceObjects(hydratedSourceObjects, resolvedSourceObject)
      useBooksStore.setState(state => ({
        sourceObjects: mergeSourceObjects(state.sourceObjects, resolvedSourceObject!)
      }))

      if (typeof exportedSourceObject.id === 'string' && exportedSourceObject.id.length > 0) {
        sourceObjectIdMap[exportedSourceObject.id] = resolvedSourceObject.id
      }
    }

    return sourceObjectIdMap
  }

  // ==============================================================================
  // UTILITY METHODS
  // ==============================================================================

  private async validateImportData(data: any): Promise<ValidationResult> {
    const supportedFormats = [
      'scholarly_book_v1',
      'scholarly_book_v2',
      'scholarly_chapter_v2', 
      'manuscript_witness_v2',
      'workflow_session_v2',
      'text_alignment_v2',
      'scholarly_app_state_v3',
      APP_STATE_BUNDLE_EXPORT_TYPE
    ]

    return {
      is_valid: true,
      format_supported: supportedFormats.includes(data.export_type),
      version_compatible: data.format_version === '2.0.0'
        || data.format_version === '3.0.0'
        || data.format_version === LEGACY_APP_STATE_BUNDLE_FORMAT_VERSION
        || data.format_version === PREVIOUS_APP_STATE_BUNDLE_FORMAT_VERSION
        || data.format_version === APP_STATE_BUNDLE_FORMAT_VERSION,
      integrity_verified: !!data.checksum,
      missing_dependencies: [],
      validation_warnings: [],
      validation_errors: []
    }
  }

  private async formatExport(data: any, format: ExportFormat): Promise<string | Blob> {
    switch (format) {
      case 'json':
        return JSON.stringify(data, null, 2)
      case 'zip':
        // TODO: Implement ZIP packaging
        return JSON.stringify(data, null, 2)
      case 'scholarly_package':
        // TODO: Implement scholarly package format
        return JSON.stringify(data, null, 2)
      default:
        return JSON.stringify(data, null, 2)
    }
  }

  private async calculateChecksum(data: string): Promise<string> {
    const encoder = new TextEncoder()
    const dataBuffer = encoder.encode(data)
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  private async calculateChecksumFromArrayBuffer(data: ArrayBuffer): Promise<string> {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  private calculateProcessingStatistics(witnesses: any[]): ProcessingStatistics {
    // TODO: Implement detailed processing statistics calculation
    return {
      total_processing_time_minutes: 0,
      average_chunk_processing_time_seconds: 0,
      total_api_calls: 0,
      total_tokens_processed: 0,
      estimated_cost_usd: 0,
      model_usage_breakdown: {},
      error_rate_percentage: 0
    }
  }

  private countTotalMicroUnits(witnesses: any[]): number {
    return witnesses.reduce((total, witness) => {
      if (witness.segmentation_result?.chunk_results) {
        return total + witness.segmentation_result.chunk_results.reduce((chunkTotal: number, chunk: any) => 
          chunkTotal + (chunk.micro_units?.length || 0), 0)
      }
      return total
    }, 0)
  }

  private calculateAverageConfidence(witnesses: any[]): number {
    // TODO: Implement confidence calculation
    return 0.8
  }

  private countWitnessMicroUnits(witness: any): number {
    if (witness.segmentation_result?.chunk_results) {
      return witness.segmentation_result.chunk_results.reduce((total: number, chunk: any) => 
        total + (chunk.micro_units?.length || 0), 0)
    }
    return 0
  }

  private calculateWitnessConfidence(witness: any): number {
    // TODO: Implement witness-specific confidence calculation
    return 0.8
  }

  private extractProcessingHistory(witness: any): any[] {
    // TODO: Extract processing history from witness data
    return []
  }

  private captureStorage(storage: Storage | undefined): Record<string, string> {
    if (!storage) {
      return {}
    }
    const snapshot: Record<string, string> = {}
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (!key || key === APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY) {
        continue
      }
      const value = storage.getItem(key)
      if (value !== null) {
        snapshot[key] = value
      }
    }
    return snapshot
  }

  private restoreStorage(storage: Storage | undefined, snapshot: Record<string, string>): void {
    if (!storage) {
      return
    }
    storage.clear()
    Object.entries(snapshot).forEach(([key, value]) => {
      if (key !== APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY) storage.setItem(key, value)
    })
  }

  private countSnapshotEntries(snapshot: Record<string, any[]>): Record<string, number> {
    return Object.fromEntries(
      Object.entries(snapshot).map(([storeName, records]) => [storeName, Array.isArray(records) ? records.length : 0])
    )
  }

  private reportBackupProgress(
    listener: AppStateBackupProgressListener | undefined,
    event: AppStateBackupProgressEvent
  ): void {
    if (!listener) return
    try {
      listener({
        ...event,
        percent: clampBackupProgressPercent(event.percent),
      })
    } catch (error) {
      console.warn('Backup progress observer failed; the backup operation will continue safely.', error)
    }
  }

  private async refreshHydratedStores(): Promise<void> {
    useAnchoredRowsStore.setState({
      chapterRows: new Map(),
      loadingChapters: new Set()
    } as any)
    useDeviceAnnotationStore.setState({
      chapterLayers: new Map(),
      witnessLayers: new Map(),
      runs: new Map()
    } as any)
    useCharacterLayerStore.setState({
      chapterLayers: new Map()
    } as any)
    useReaderTextStructureStore.setState({
      layers: new Map(),
      loadingLayerIds: new Set()
    } as any)
    useStructuralRangeStore.setState({
      layers: new Map(),
      loadingChapterIds: new Set()
    } as any)
    useAnnotationResearchNoteStore.setState({
      notesByScope: new Map(),
      loadingScopeIds: new Set()
    } as any)
    useAnalyticalTaskStore.setState({
      taskSets: new Map(),
      loadingWitnessIds: new Set(),
      loadingAll: false
    } as any)
    useCollationStore.setState({
      collations: new Map(),
      currentCollation: null,
      versions: new Map(),
      isLoading: false,
      error: null
    } as any)
    useBooksStore.setState({
      books: [],
      chapters: [],
      witnesses: [],
      sourceObjects: [],
      loading: false,
      error: null,
      dbInitialized: false
    } as any)
    await useBooksStore.getState().initializeDB()
    const [characterLayers, readerStructureLayers, structuralRangeLayers, annotationNotes, analyticalTaskSets] = await Promise.all([
      dbStorage.getAll('chapter_character_layers'),
      dbStorage.getAll('reader_text_structure_layers'),
      dbStorage.getAll('chapter_structural_layers'),
      dbStorage.getAll('annotation_research_notes'),
      dbStorage.getAll('analytical_task_sets')
    ])
    useCharacterLayerStore.setState({
      chapterLayers: new Map(characterLayers.map(layer => [layer.chapter_id, layer]))
    } as any)
    useReaderTextStructureStore.setState({
      layers: new Map(readerStructureLayers.map(layer => [layer.id, layer])),
      loadingLayerIds: new Set()
    } as any)
    useStructuralRangeStore.setState({
      layers: new Map(structuralRangeLayers.map(layer => [layer.chapterId, layer])),
      loadingChapterIds: new Set()
    } as any)
    const notesByScope = new Map<string, typeof annotationNotes>()
    for (const note of annotationNotes) {
      const scope = `${note.chapterId}::${note.sourceWitnessId}`
      const notes = notesByScope.get(scope) || []
      notes.push(note)
      notesByScope.set(scope, notes)
    }
    notesByScope.forEach(notes => notes.sort((left, right) => (
      left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id)
    )))
    useAnnotationResearchNoteStore.setState({
      notesByScope,
      loadingScopeIds: new Set()
    } as any)
    useAnalyticalTaskStore.setState({
      taskSets: new Map(analyticalTaskSets
        .map(taskSet => migrateAnalyticalTaskSet(taskSet))
        .map(taskSet => [taskSet.id, taskSet])),
      loadingWitnessIds: new Set(),
      loadingAll: false
    } as any)
    await useCollationStore.getState().loadCollations()
    useSessionStore.getState().refreshSessions()
  }

  private async buildAppStateBackupZip(
    data: AppStateBackupExport,
    legacyCollationStores: LegacyCollationSnapshot,
    onProgress?: AppStateBackupProgressListener,
    inventory?: AppStateBackupWorkspaceInventory
  ): Promise<Blob> {
    const zip = new JSZip()
    const files: AppStateBundleFileRecord[] = []
    const checksums: AppStateBundleChecksums = { files: {} }
    const { portableStores, forensicStores } = this.partitionPrimaryStores(data.primary_indexed_db.stores || {})
    this.reportBackupProgress(onProgress, {
      operation: 'export',
      phase: 'packing-backup',
      percent: 30,
      message: 'Preparing the backup package',
      detail: 'Work contents are being organized and protected for download.',
      inventory,
    })

    await this.addJsonBundleFile(
      zip,
      files,
      checksums,
      'portable/route.json',
      data.route_snapshot,
      'portable',
      'route'
    )
    await this.addJsonBundleFile(
      zip,
      files,
      checksums,
      'portable/browser-storage.json',
      data.browser_storage,
      'portable',
      'browser'
    )
    await this.addStoreBundleFiles(zip, files, checksums, 'portable', 'primary', portableStores)
    this.reportBackupProgress(onProgress, {
      operation: 'export',
      phase: 'packing-backup',
      percent: 52,
      message: 'Works and reading material are packed',
      inventory,
    })
    await this.addStoreBundleFiles(zip, files, checksums, 'portable', 'resume', data.resume_indexed_db.stores || {})
    await this.addStoreBundleFiles(zip, files, checksums, 'forensic', 'primary', forensicStores)
    await this.addStoreBundleFiles(
      zip,
      files,
      checksums,
      'compatibility',
      'legacy_collation',
      legacyCollationStores
    )
    this.reportBackupProgress(onProgress, {
      operation: 'export',
      phase: 'packing-backup',
      percent: 70,
      message: 'Editing history and recovery information are packed',
      inventory,
    })

    const manifest: AppStateBundleManifest = {
      format_version: APP_STATE_BUNDLE_FORMAT_VERSION,
      export_type: APP_STATE_BUNDLE_EXPORT_TYPE,
      store_catalog_version: APP_STATE_STORE_CATALOG_VERSION,
      export_timestamp: data.export_timestamp,
      exported_by: data.exported_by,
      metadata: {
        application_version: data.metadata?.application_version || '4.2.0',
        platform: data.metadata?.platform || (typeof navigator !== 'undefined' ? navigator.platform : 'unknown'),
        total_size_bytes: data.metadata?.total_size_bytes,
        estimated_import_time: data.metadata?.estimated_import_time,
        compatibility_notes: [
          'Portable state restores browser-persisted Workbench and Variorum state.',
          'The compatibility section preserves legacy CollationDB records without importing them into anchored rows.',
          'Forensic archive preserves full Variorum editing history for later analysis and training.',
          'Server-saved Build runs and matrix sessions are operational artifacts and are not included in this browser bundle.'
        ]
      },
      route_snapshot: data.route_snapshot,
      portable_summary: {
        primary_store_counts: this.countSnapshotEntries(portableStores),
        resume_store_counts: this.countSnapshotEntries(data.resume_indexed_db.stores || {}),
        local_storage_keys: Object.keys(data.browser_storage?.local_storage || {}).length,
        session_storage_keys: Object.keys(data.browser_storage?.session_storage || {}).length
      },
      forensic_summary: {
        primary_store_counts: this.countSnapshotEntries(forensicStores),
        resume_store_counts: {},
        local_storage_keys: 0,
        session_storage_keys: 0
      },
      compatibility_summary: {
        legacy_collation_store_counts: this.countSnapshotEntries(legacyCollationStores)
      },
      operational_artifacts: {
        saved_build_runs: {
          included: false,
          recovery_scope: 'operator-dr',
          source: 'session_data/staged_reference_alignment'
        },
        matrix_sessions: {
          included: false,
          recovery_scope: 'operator-dr',
          source: 'data/matrix_sessions'
        }
      },
      files
    }

    zip.file('manifest.json', JSON.stringify(manifest, null, 2))
    zip.file('checksums.json', JSON.stringify(checksums, null, 2))

    const blob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
      streamFiles: true,
      mimeType: 'application/zip'
    }, metadata => {
      this.reportBackupProgress(onProgress, {
        operation: 'export',
        phase: 'packing-backup',
        percent: 70 + (metadata.percent * 0.28),
        message: 'Finishing the download package',
        detail: `${Math.round(metadata.percent)}% of the package compressed.`,
        inventory,
      })
    })
    this.reportBackupProgress(onProgress, {
      operation: 'export',
      phase: 'ready',
      percent: 98,
      message: 'Backup package is ready to download',
      inventory,
    })
    return blob
  }

  private partitionPrimaryStores(
    stores: Record<string, any[]>
  ): {
    portableStores: Record<string, any[]>
    forensicStores: Record<string, any[]>
  } {
    const portableStores = Object.fromEntries(
      PORTABLE_PRIMARY_STORE_NAMES.map(store => [store, Array.isArray(stores[store]) ? stores[store] : []])
    )
    const forensicStores = Object.fromEntries(
      FORENSIC_PRIMARY_STORE_NAMES.map(store => [store, Array.isArray(stores[store]) ? stores[store] : []])
    )

    return { portableStores, forensicStores }
  }

  private async addJsonBundleFile(
    zip: JSZip,
    files: AppStateBundleFileRecord[],
    checksums: AppStateBundleChecksums,
    path: string,
    payload: unknown,
    section: 'portable' | 'forensic',
    storageScope: 'browser' | 'route'
  ): Promise<void> {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    zip.file(path, blob)
    files.push({
      path,
      section,
      storage_scope: storageScope,
      kind: 'json',
      record_count: 1,
      bytes: blob.size
    })
    checksums.files[path] = await this.calculateChecksumFromArrayBuffer(await this.readBlobArrayBuffer(blob))
  }

  private async addStoreBundleFiles(
    zip: JSZip,
    files: AppStateBundleFileRecord[],
    checksums: AppStateBundleChecksums,
    section: 'portable' | 'forensic' | 'compatibility',
    storageScope: 'primary' | 'resume' | 'legacy_collation',
    stores: Record<string, any[]>
  ): Promise<void> {
    for (const [storeName, records] of Object.entries(stores)) {
      const chunks = this.buildNdjsonChunks(records)
      for (let index = 0; index < chunks.length; index += 1) {
        const chunk = chunks[index]
        const path = `${section}/${storageScope}/${storeName}/part-${String(index + 1).padStart(4, '0')}.ndjson`
        zip.file(path, chunk.blob)
        files.push({
          path,
          section,
          storage_scope: storageScope,
          kind: 'ndjson',
          record_count: chunk.recordCount,
          bytes: chunk.blob.size,
          store_name: storeName
        })
        checksums.files[path] = await this.calculateChecksumFromArrayBuffer(await this.readBlobArrayBuffer(chunk.blob))
      }
    }
  }

  private buildNdjsonChunks(records: any[]): Array<{ blob: Blob; recordCount: number }> {
    if (!Array.isArray(records) || records.length === 0) {
      return []
    }

    const encoder = new TextEncoder()
    const chunks: Array<{ blob: Blob; recordCount: number }> = []
    let parts: string[] = []
    let currentBytes = 0
    let currentCount = 0

    records.forEach((record) => {
      const line = `${JSON.stringify(record)}\n`
      const lineBytes = encoder.encode(line).length

      if (currentCount > 0 && currentBytes + lineBytes > APP_STATE_BUNDLE_CHUNK_BYTES) {
        chunks.push({
          blob: new Blob(parts, { type: 'application/x-ndjson' }),
          recordCount: currentCount
        })
        parts = []
        currentBytes = 0
        currentCount = 0
      }

      parts.push(line)
      currentBytes += lineBytes
      currentCount += 1
    })

    if (currentCount > 0) {
      chunks.push({
        blob: new Blob(parts, { type: 'application/x-ndjson' }),
        recordCount: currentCount
      })
    }

    return chunks
  }

  private async readBundleJson<T>(
    zip: JSZip,
    path: string,
    checksums?: AppStateBundleChecksums,
    expectedBytes?: number
  ): Promise<T> {
    const file = zip.file(path)
    if (!file) {
      throw new Error(`Bundle entry not found: ${path}`)
    }
    const data = await file.async('arraybuffer')
    if (expectedBytes !== undefined && data.byteLength !== expectedBytes) {
      throw new Error(`Byte count mismatch for bundle entry: ${path}`)
    }
    await this.verifyBundleChecksum(path, data, checksums)
    return JSON.parse(new TextDecoder().decode(new Uint8Array(data))) as T
  }

  private async readBundleNdjson(
    zip: JSZip,
    path: string,
    checksums?: AppStateBundleChecksums,
    expectedBytes?: number
  ): Promise<any[]> {
    const file = zip.file(path)
    if (!file) {
      throw new Error(`Bundle entry not found: ${path}`)
    }
    const data = await file.async('arraybuffer')
    if (expectedBytes !== undefined && data.byteLength !== expectedBytes) {
      throw new Error(`Byte count mismatch for bundle entry: ${path}`)
    }
    await this.verifyBundleChecksum(path, data, checksums)

    const text = new TextDecoder().decode(new Uint8Array(data))
    const trimmed = text.trim()
    if (!trimmed) {
      return []
    }

    return trimmed
      .split('\n')
      .filter(line => line.trim().length > 0)
      .map(line => JSON.parse(line))
  }

  private async verifyBundleChecksum(
    path: string,
    data: ArrayBuffer,
    checksums?: AppStateBundleChecksums
  ): Promise<void> {
    const expected = checksums?.files?.[path]
    if (!expected) {
      if (checksums) {
        throw new Error(`Missing checksum for bundle entry: ${path}`)
      }
      return
    }

    const actual = await this.calculateChecksumFromArrayBuffer(data)
    if (actual !== expected) {
      throw new Error(`Checksum mismatch for bundle entry: ${path}`)
    }
  }

  private async readBlobArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    if (typeof blob.arrayBuffer === 'function') {
      return blob.arrayBuffer()
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as ArrayBuffer)
      reader.onerror = () => reject(reader.error || new Error('Failed to read blob'))
      reader.readAsArrayBuffer(blob)
    })
  }

  private isZipPayload(fileContent: ArrayBuffer): boolean {
    const bytes = new Uint8Array(fileContent)
    return bytes.length >= 4
      && bytes[0] === 0x50
      && bytes[1] === 0x4b
      && [0x03, 0x05, 0x07].includes(bytes[2])
      && [0x04, 0x06, 0x08].includes(bytes[3])
  }

  private buildAppStateBackupBlob(data: AppStateBackupExport): Blob {
    const parts: BlobPart[] = ['{']

    this.appendJsonProperty(parts, 'format_version', data.format_version)
    this.appendJsonProperty(parts, 'export_type', data.export_type)
    this.appendJsonProperty(parts, 'export_timestamp', data.export_timestamp)
    this.appendJsonProperty(parts, 'exported_by', data.exported_by ?? null)
    this.appendJsonProperty(parts, 'metadata', data.metadata ?? null)
    this.appendJsonProperty(parts, 'route_snapshot', data.route_snapshot)

    parts.push('"primary_indexed_db":{"stores":')
    this.appendSnapshotStores(parts, data.primary_indexed_db.stores)
    parts.push('},')

    parts.push('"resume_indexed_db":{"stores":')
    this.appendSnapshotStores(parts, data.resume_indexed_db.stores)
    parts.push('},')

    this.appendJsonProperty(parts, 'browser_storage', data.browser_storage)
    this.appendJsonProperty(parts, 'export_summary', data.export_summary, false)
    parts.push('}')

    return new Blob(parts, { type: 'application/json' })
  }

  private appendJsonProperty(
    parts: BlobPart[],
    key: string,
    value: unknown,
    includeTrailingComma: boolean = true
  ): void {
    parts.push(`${JSON.stringify(key)}:${JSON.stringify(value)}`)
    if (includeTrailingComma) {
      parts.push(',')
    }
  }

  private appendSnapshotStores(parts: BlobPart[], stores: Record<string, any[]>): void {
    const entries = Object.entries(stores)
    parts.push('{')
    entries.forEach(([storeName, records], storeIndex) => {
      parts.push(`${JSON.stringify(storeName)}:[`)
      records.forEach((record, recordIndex) => {
        parts.push(JSON.stringify(record))
        if (recordIndex < records.length - 1) {
          parts.push(',')
        }
      })
      parts.push(']')
      if (storeIndex < entries.length - 1) {
        parts.push(',')
      }
    })
    parts.push('}')
  }
}

// Export singleton instance
export const exportImportManager = new ExportImportManager()
